/**
 * WitnessesTab — the prosecutor's "Who's Who".
 *
 * Two jobs: (1) identify the key individuals in the case, (2) link each to their
 * evidence (statements / reports / media / events). It complements the
 * Chronology tab and reuses its patterns: browse-first, a human-review gate
 * (suggested → accepted / dismissed), and optimistic mutations that reconcile
 * with the server's authoritative response.
 *
 * Layout: a roster RAIL on the left (accepted witnesses + a "Suggested" tray
 * fed by the suggest endpoint) and a person DETAIL panel on the right (identity
 * header + an evidence-type rail rendered FROM the dossier's `evidence.registry`,
 * each item citation-clickable into the document viewer).
 *
 * Lazy-mounted (every action is a synchronous endpoint — no background polling).
 * Phase 1 = jobs 1 & 2 only; merge tray / split / export are later phases.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { api } from '../../api';
import EntityDossierModal from '../../components/EntityDossierModal';
import RoleProfileCards from '../../components/RoleProfileCards';
import WitnessExportMenu from '../../components/WitnessExportMenu';
import { useUserProfile } from '../../hooks/useUserProfile';
import useWitnessRoster from '../../hooks/useWitnessRoster';
import useWitnessCleanup from '../../hooks/useWitnessCleanup';
import {
  compareWitnesses,
  compareWitnessesBy,
  dedupeCitationsByFamily,
  evidenceGlyph,
  evidenceCitation,
  referenceCitation,
  participantName,
  WITNESS_SORT,
  WITNESS_SORT_LABELS,
} from '../../domain/witnessEvidence';
import { entityBadges, entityAbstraction } from '../../domain/witnessLayers';

// Human words for the abstraction ladder — "PERSON ENTITY" read as jargon.
const ABSTRACTION_LABELS = {
  Person: 'Person',
  Partial: 'Partial profile',
  Fragment: 'Single mention',
};
import { isBelowDeep, tierLabel } from '../../domain/searchTier';
import FilterRail from '../../components/inventory/FilterRail';
import {
  WITNESS_AXES,
  applyWitnessFilters,
  getWitnessPickerRows,
  readRosterFiltersFromUrl,
  writeRosterFiltersToUrl,
} from '../../contexts/witnessFilterAxes';


// Bulk-cleanup size estimate — the LLM safety valve. The unit of work is
// candidate CLUSTERS (Arc adjudicates resolver-seeded clusters, not blind N²).
// Evidence VOLUME no longer gates: the gather now reads only a bounded SAMPLE
// per person (individual_evidence sample=True), so a few very heavy witnesses
// are cheap — cost scales with how many people/clusters, not their doc counts.
// See WITNESS_CLEANUP_WORKFLOW_PLAN §2.
//   green  — fits one iteration
//   amber  — Arc will auto-batch into review waves
//   red    — too many clusters; narrow with filters first
const CLEANUP_GREEN = { clusters: 20 };
const CLEANUP_RED = { clusters: 80 };
const CLEANUP_BATCH_CLUSTERS = 20;

const OP_LABEL = { merge: 'Merge', accept: 'Accept', reject: 'Reject' };

// Extractor quality labels treated as roster noise (hidden by default). The
// backend already auto-hides high-confidence `junk`; these are what it keeps but
// a lawyer doesn't want cluttering the default view. `unclassified` is NOT here
// — an unjudged entity may be a real person.
const NOISE_QUALITY = new Set(['role_reference', 'junk']);

function estimateCleanupSize(rows) {
  const count = rows.length;
  const citations = rows.reduce((n, r) => n + (r.evidence_count || 0), 0);
  // Cluster proxy: union witnesses linked by `possible_duplicate_of`; anyone
  // unlinked counts as their own candidate. A cheap, no-LLM pre-flight number.
  const ids = new Set(rows.map((r) => r.id));
  const seen = new Set();
  let clusters = 0;
  for (const r of rows) {
    if (seen.has(r.id)) continue;
    clusters += 1;
    seen.add(r.id);
    const dupId = r.possible_duplicate_of?.witness_id;
    if (dupId && ids.has(dupId)) seen.add(dupId);
  }
  let zone = 'green';
  if (clusters > CLEANUP_RED.clusters) zone = 'red';
  else if (clusters > CLEANUP_GREEN.clusters) zone = 'amber';
  const batches = Math.max(1, Math.ceil(clusters / CLEANUP_BATCH_CLUSTERS));
  return { count, clusters, citations, zone, batches };
}

/* Clickable citation into the document viewer. Same in-app navigation contract
 * as TensionsTab / EntityDossierModal: a full reload re-mounts AuthProvider and
 * the viewer fetch 401s before the token is ready, signing the user out, so we
 * navigate() to open the overlay in-tab. Modifier-clicks fall through to the
 * browser (open in new tab). */
function CitationLink({ item, pathname }) {
  const navigate = useNavigate();
  const cite = evidenceCitation(item, pathname);
  if (!cite) return <span className="ed-src">{item.filename || 'source'}</span>;
  return (
    <a
      href={cite.href}
      className="citation-link wt-src"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(cite.href);
      }}
    >📄 {cite.label}</a>
  );
}

/* ── Roster rail ── */

/* Manual "Merge into…" picker. Lists the roster targets this card can fold INTO
 * (accepted witnesses first, then other suggestions, never the card itself), so
 * the user can resolve a duplicate the system can't guess (e.g. "drone pilot" →
 * "Officer Garcia"). Portaled to document.body — the witnesses grid is a
 * transformed/overflow ancestor that would clip an in-flow popover (the React-18
 * portal gotcha). Closes on select, outside-click, or Escape. */
function MergePicker({ rect, targets, onPick, onClose, title = 'Merge into…' }) {
  const [query, setQuery] = useState('');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q = query.trim().toLowerCase();
  const shown = q
    ? targets.filter((t) => (t.display_name || '').toLowerCase().includes(q))
    : targets;

  // Flip above the anchor when there's no room below (the references-rail
  // buttons sit at the BOTTOM of the viewport — top-anchoring there pushes the
  // whole list off-screen). position:fixed, so viewport coords are the truth.
  const POP_H = 360; // head + search + max list height + padding
  const flipUp = rect.bottom + POP_H > window.innerHeight && rect.top > POP_H;
  const pos = flipUp
    ? { bottom: window.innerHeight - rect.top + 4 }
    : { top: rect.bottom + 4 };
  pos.left = Math.max(12, Math.min(rect.left, window.innerWidth - 292));

  return createPortal((
    <div className="wt-merge-overlay" onMouseDown={onClose}>
      <div
        className="wt-merge-popover"
        style={pos}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="wt-merge-pop-head">{title}</div>
        <input
          className="input wt-merge-search"
          autoFocus
          placeholder="Filter witnesses…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="wt-merge-list">
          {shown.length === 0 && <div className="ed-muted wt-merge-empty">No matching witnesses.</div>}
          {shown.map((t) => (
            <button
              type="button"
              key={t.id}
              className="wt-merge-item"
              onClick={() => onPick(t)}
            >
              <span className="wt-merge-item-name">{t.display_name}</span>
              <span className={`wt-merge-item-tag wt-merge-item-${t.review_state}`}>
                {t.review_state === 'accepted' ? 'on roster' : 'suggested'}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  ), document.body);
}

/* Abstraction badges — rendered from the layer registry, so the card's
   abstraction level (fragment → Person) shows itself. Shared by the index card
   and the Workbench header. Adding an enrichment = one entry in witnessLayers. */
function EntityBadges({ witness, compact = false }) {
  const ir = witness.identity_review;
  // Only flag identity provenance once a person is ACCEPTED — a still-"Suggested"
  // row is already visibly un-acted-on, so ⚠ there is just noise. The signal that
  // matters: an accepted witness whose grouping was auto-done and never verified.
  const accepted = witness.review_state !== 'suggested';
  return (
    <span className={`wb-badges ${compact ? 'wb-badges-compact' : ''}`}>
      {entityBadges(witness).map((b) => (
        <span key={b.key} className={`wb-badge wb-badge-${b.cls}`} title={b.tip || b.label}>
          {b.glyph ? `${b.glyph} ` : ''}{b.label}
        </span>
      ))}
      {accepted && ir?.state === 'unreviewed' && (
        // E2 — neutral provenance, not a chore: Arc grouped this person; the
        // correction gesture is ✂ on a wrong reference, not a review pass.
        <span
          className="wb-badge wb-badge-unreviewed"
          title={`Arc matched ${ir.unreviewed} reference${ir.unreviewed === 1 ? '' : 's'} into this person automatically. Open References and ✂ any that don't belong — or Confirm this person to vouch for the grouping.`}
        >🤖{compact ? '' : ' Auto-matched'}</span>
      )}
      {!compact && accepted && ir?.state === 'confirmed' && (
        <span className="wb-badge wb-badge-confirmed" title="Identity human-confirmed">✓ Confirmed</span>
      )}
    </span>
  );
}

/* ── Index card ──
   The navigational row in the unified index. Purely "pick this one" — ALL
   actions live in the Workbench. Renders typed abstraction badges from the
   layer registry + a bulk-select checkbox. (Workbench step W1.) */
function IndexCard({ witness, active, selected, onSelect, onToggleSelect }) {
  return (
    <div className={`wb-card ${active ? 'wb-card-active' : ''} ${selected ? 'wb-card-selected' : ''}`}>
      <input
        type="checkbox"
        className="wb-card-check"
        checked={selected}
        onChange={() => onToggleSelect(witness)}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Select ${witness.display_name}`}
        title="Select for a batch action"
      />
      <button type="button" className="wb-card-main" onClick={() => onSelect(witness)}>
        <span className="wb-card-name">{witness.display_name}</span>
        <EntityBadges witness={witness} compact />
      </button>
    </div>
  );
}

/* ── Person detail panel ── */

/* How this person relates to one evidence item — their OWN words vs testimony
 * ABOUT them (and the legal flavour: admission / denial / ID). Backend derives
 * role + friendly label (evidence.py claim_relationship/event_relationship); the
 * chip just adds the icon. Absent on plain document rows → renders nothing. */
const REL_ICON = { made: '🗣', about: '👤', participant: '📅', location: '📍' };
function RelationshipChip({ relationship }) {
  if (!relationship || !relationship.label) return null;
  const role = relationship.role;
  const icon = REL_ICON[role] || '';
  const title = role === 'made'
    ? 'This person asserted this — their own words'
    : role === 'about'
      ? `Someone else's statement naming this person${relationship.asserter_name ? ` — by ${relationship.asserter_name}` : ''}`
      : role === 'location'
        ? 'This happened at / to this person'
        : 'This person took part in this event';
  return (
    <span className={`wt-rel-chip wt-rel-${role}`} title={title}>
      {icon && <span className="wt-rel-ico">{icon}</span>}{relationship.label}
    </span>
  );
}

/* The subset Resolve button — one action, always the calibrated policy (the
 * learned zero-false-merge band; the aggressiveness dial is gone everywhere,
 * WITNESS_GOLD_CALIBRATION_PLAN). Hands the selection to the cleanup run. */
function SweepButton({ count, zone, disabled, onSweep }) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-primary wt-sweep-go"
      disabled={disabled || zone === 'red'}
      title={zone === 'red'
        ? 'Too many for one pass — narrow with the filters first'
        : 'Resolve the selected people with Arc — it proposes merges you approve'}
      onClick={() => onSweep()}
    >✨ Resolve {count} selected</button>
  );
}

/* Group the selected people for browsing: union any two linked by
   `possible_duplicate_of` (within the selection) so likely-dups sit together;
   everyone else is a singleton. Pure — no fetch (roster rows already carry the
   link). Mirrors the resolver-seeded clustering estimateCleanupSize() uses. */
function groupSelectedForBrowse(people) {
  const byId = new Map(people.map((w) => [w.id, w]));
  const parent = new Map(people.map((w) => [w.id, w.id]));
  const find = (x) => { let r = x; while (parent.get(r) !== r) r = parent.get(r); return r; };
  const union = (a, b) => { const ra = find(a); const rb = find(b); if (ra !== rb) parent.set(ra, rb); };
  for (const w of people) {
    const dup = w.possible_duplicate_of?.witness_id;
    if (dup && byId.has(dup)) union(w.id, dup);
  }
  const groups = new Map();
  for (const w of people) {
    const r = find(w.id);
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(w);
  }
  const dupGroups = [];
  const others = [];
  for (const g of groups.values()) {
    if (g.length >= 2) dupGroups.push(g); else others.push(g[0]);
  }
  return { dupGroups, others };
}

/* One person in the Batch Overview — click the body (or Open) to drill into its
   full DetailPanel; ✕ removes it from the selection. */
function BatchCard({ witness, onOpen, onUntick }) {
  return (
    <div className="wb-batch-card">
      <button type="button" className="wb-batch-card-main" onClick={() => onOpen(witness)}>
        <span className="wb-card-name">{witness.display_name || 'unnamed'}</span>
        <EntityBadges witness={witness} compact />
      </button>
      <div className="wb-batch-card-actions">
        <button type="button" className="btn btn-sm" onClick={() => onOpen(witness)}>Open</button>
        <button
          type="button"
          className="btn btn-sm wb-batch-untick"
          title="Remove from selection"
          aria-label={`Remove ${witness.display_name || 'person'} from selection`}
          onClick={() => onUntick(witness)}
        >✕</button>
      </div>
    </div>
  );
}

/* The Batch Overview — the workspace surface for a multi-selection. Browse the
   selected people (likely-dups grouped together), open any into its DetailPanel,
   remove ones that don't belong, and resolve the batch — all in one place. The
   single-card workspace is untouched; this only appears when >1 is selected and
   you haven't drilled into a card. (WITNESS_UNIFIED_RESOLVE_PLAN — browse-first.) */
function BatchOverview({ people, estimate, cleanupActive, onOpen, onUntick, onClear, onSweep, onMergeAll }) {
  const { dupGroups, others } = useMemo(() => groupSelectedForBrowse(people), [people]);
  return (
    <div className="wb-batch">
      <div className="wb-batch-head">
        <div className="wb-batch-title">
          <b>Selected ({people.length})</b>
          <span className="ed-muted"> — browse the batch, then resolve duplicates.</span>
        </div>
        <button type="button" className="btn btn-sm" onClick={onClear}>Clear selection</button>
      </div>
      <div className={`wb-batch-actions wt-zone-${estimate.zone}`}>
        <SweepButton
          count={estimate.count}
          zone={estimate.zone}
          disabled={cleanupActive}
          onSweep={onSweep}
        />
        <span className={`wt-zone-pill wt-zone-pill-${estimate.zone}`}>
          {estimate.zone === 'green' && 'Good batch size'}
          {estimate.zone === 'amber' && `Large — ~${estimate.batches} waves`}
          {estimate.zone === 'red' && 'Too many — narrow with filters'}
        </span>
        {people.length >= 2 && (
          <div className="wb-batch-merge-row">
            <span className="ed-muted">Already know they’re the same?</span>
            <button
              type="button"
              className="btn btn-sm wb-batch-merge"
              disabled={cleanupActive}
              title="Combine the selected people into one — deterministic, no Arc. Survivor = the most-documented. Undo from Recently merged."
              onClick={onMergeAll}
            >⧉ Merge {people.length} into one</button>
          </div>
        )}
      </div>
      {dupGroups.length > 0 && (
        <div className="wb-batch-section">
          <div className="wb-batch-shead">⚠ Likely the same person</div>
          {dupGroups.map((g, i) => (
            <div className="wb-batch-group" key={`dup-${g[0].id}-${i}`}>
              {g.map((w) => <BatchCard key={w.id} witness={w} onOpen={onOpen} onUntick={onUntick} />)}
            </div>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <div className="wb-batch-section">
          {dupGroups.length > 0 && <div className="wb-batch-shead">Others in selection</div>}
          <div className="wb-batch-grid">
            {others.map((w) => <BatchCard key={w.id} witness={w} onOpen={onOpen} onUntick={onUntick} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function EvidenceItem({ item, pathname }) {
  if (item.kind === 'claim') {
    return (
      <div className="wt-ev-row">
        <div className="wt-ev-line">
          <RelationshipChip relationship={item.relationship} />
          <b>{item.asserter?.canonical_name || 'Someone'}</b>
          {item.claim_text ? `: ${item.claim_text}` : ''}
        </div>
        {item.supporting_quote && <div className="wt-ev-quote">“{item.supporting_quote}”</div>}
        <CitationLink item={item} pathname={pathname} />
      </div>
    );
  }
  if (item.kind === 'event') {
    const names = (item.participants || []).map(participantName).filter(Boolean);
    return (
      <div className="wt-ev-row">
        <div className="wt-ev-line">
          <RelationshipChip relationship={item.relationship} />
          {item.occurred_at_iso && <span className="wt-ev-date">{String(item.occurred_at_iso).slice(0, 10)} · </span>}
          <b>{(item.event_type || 'event').replace(/_/g, ' ').toLowerCase()}</b>
          {item.description ? ` — ${item.description}` : ''}
        </div>
        {names.length > 0 && <div className="wt-ev-meta">{names.join(', ')}</div>}
        <CitationLink item={item} pathname={pathname} />
      </div>
    );
  }
  // Doc item (reports / media). The citation link already shows the filename
  // (and page), so don't print the name a second time as a plain line.
  return (
    <div className="wt-ev-row">
      <CitationLink item={item} pathname={pathname} />
    </div>
  );
}

/* One reference — this person as named in ONE document. The checkbox selects it
 * to move out (it's actually someone else); clicking the row expands that single
 * document's evidence, and a chip shows how the reference got here (auto-matched
 * by the system vs human-confirmed). */
/* mm:ss (or h:mm:ss) for a media mention's time offset. */
function fmtMentionTime(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const r = s % 60;
  const mm = String(m).padStart(h ? 2 : 1, '0'); const rr = String(r).padStart(2, '0');
  return h ? `${h}:${mm}:${rr}` : `${mm}:${rr}`;
}

/* The mention in context — a reference row's evidence floor when the document
 * names this person but no statements were extracted. Snippet + locator, with
 * the same pinpoint link as the row header so "read in context" lands on the
 * highlighted name. */
function MentionContext({ fragment, pinpoint, navigate }) {
  if (!fragment.mention_snippet) {
    return (
      <div className="ed-muted">
        Referenced in this document, but no statements were extracted — open
        the document above to read the mention.
      </div>
    );
  }
  const loc = fragment.mention_page != null
    ? `p. ${fragment.mention_page}`
    : (fragment.mention_time != null ? `at ${fmtMentionTime(fragment.mention_time)}` : null);
  return (
    <div className="wt-mention-ctx">
      <div className="ed-muted">Referenced in passing{loc ? ` — ${loc}` : ''}:</div>
      <blockquote className="wt-mention-quote">“{fragment.mention_snippet}”</blockquote>
      {pinpoint && (
        <a
          href={pinpoint.href}
          className="citation-link"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            navigate(pinpoint.href);
          }}
        >Read in context →</a>
      )}
    </div>
  );
}

function ReferenceRow({ caseId, witnessId, fragment, selected, onToggleSelect, pathname, onSeparate, separating, defaultOpen = false }) {
  const navigate = useNavigate();
  const pinpoint = referenceCitation(fragment, pathname);
  const [open, setOpen] = useState(defaultOpen);
  const [phase, setPhase] = useState('idle'); // idle | loading | ready | error
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState('');
  const loadedRef = useRef(false); // load once per open; phase NOT in deps

  // Mention-only references need no evidence fetch: the snippet travels on
  // the fragment itself, so expanding is instant and free. Only rows with
  // statements load their bucketized evidence.
  const mentionOnly = (fragment.evidence_count || 0) === 0;

  useEffect(() => {
    if (!open || loadedRef.current || mentionOnly) return;
    loadedRef.current = true;
    let cancelled = false;
    (async () => {
      setPhase('loading'); setErr('');
      try {
        const d = await api.get(
          `/cases/${caseId}/witnesses/${witnessId}/members/${fragment.entity_id}/evidence`,
        );
        if (cancelled) return;
        setEv(d.evidence); setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setErr(e.message || 'Could not load evidence.'); setPhase('error');
        loadedRef.current = false; // retry on reopen
      }
    })();
    return () => { cancelled = true; };
  }, [open, caseId, witnessId, fragment.entity_id, mentionOnly]);

  return (
    <div className={`wt-frag-wrap ${open ? 'wt-frag-open' : ''}`}>
      <div className="wt-frag">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
          title="Select if this reference is actually a different person"
        />
        <button
          type="button"
          className="wt-frag-main"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          title="Show what this document says"
        >
          <span className="wt-frag-caret">{open ? '▾' : '▸'}</span>
          <span className="wt-frag-name">{fragment.name || 'unnamed'}</span>
          {fragment.role_display && <span className="wt-frag-role">{fragment.role_display}</span>}
        </button>
        {/* Pinpoint: open the document scrolled to where this person is named
            (chunk-granular — lands on the paragraph). Rendered outside the
            expand button because anchors can't nest in a button. Same in-tab
            navigate() contract as CitationLink (a full reload 401s the viewer
            before the token is ready); modifier-clicks open in a new tab.
            Falls back to a plain filename span when there's no linkable doc. */}
        {pinpoint ? (
          <a
            href={pinpoint.href}
            className="citation-link wt-frag-doc"
            title="Open the document where this person is named"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              navigate(pinpoint.href);
            }}
          >📄 {pinpoint.label}</a>
        ) : (
          fragment.filename && <span className="wt-frag-doc">📄 {fragment.filename}</span>
        )}
        {fragment.doc_date && (
          <span
            className="ed-muted wt-frag-date"
            title="Earliest dated event sourced from this document — references are ordered on the case timeline"
          >
            {new Date(fragment.doc_date).toLocaleDateString(undefined,
              { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
        )}
        {fragment.provenance === 'auto' && (
          <span className="wb-badge wb-badge-unreviewed" title="Auto-matched by the system — not yet confirmed">Auto-matched</span>
        )}
        {fragment.provenance === 'confirmed' && (
          <span className="wb-badge wb-badge-confirmed" title="You confirmed this reference belongs to this person">✓ Confirmed</span>
        )}
        {fragment.deep_read && fragment.deep_read.outcome === 'nothing_substantive' && (
          <span className="wb-badge" title="Arc read this document for this person and found nothing substantive to add">Read — nothing substantive</span>
        )}
        {fragment.deep_read && fragment.deep_read.outcome === 'statements_promoted' && (
          <span className="wb-badge wb-badge-confirmed" title="Arc read this document and added quote-verified statements">
            ✓ {fragment.deep_read.statements_found} statement{fragment.deep_read.statements_found === 1 ? '' : 's'} added
          </span>
        )}
        {isBelowDeep(fragment.tier) && fragment.tier !== 'excluded' && (
          <span
            className="wb-badge wb-badge-tier"
            title={`This document is ${tierLabel(fragment.tier).toLowerCase()}-indexed — deep search may surface more mentions of this person. Open it and run deep search to check.`}
          >🔎 {tierLabel(fragment.tier)}</span>
        )}
        <span className="wt-frag-ev">
          {fragment.evidence_count > 0
            ? `${fragment.evidence_count} statement${fragment.evidence_count === 1 ? '' : 's'}`
            : 'mention only'}
        </span>
        {onSeparate && (
          // E2 — the one-click correction: ejects this reference into its own
          // person with durable NOT_SAME (no automatic path can refold it)
          // and teaches the matcher. Hidden on single-reference cards.
          <button
            type="button"
            className="btn btn-sm wt-frag-separate"
            disabled={separating}
            onClick={(e) => { e.stopPropagation(); onSeparate(fragment.entity_id); }}
            title="Not this person — move this reference out permanently. It will never be automatically merged back."
            style={{ marginLeft: 6, fontSize: 12, color: separating ? '#94a3b8' : '#dc2626' }}
          >{separating ? '…' : '✂'}</button>
        )}
      </div>
      {open && (
        <div className="wt-frag-evidence">
          {mentionOnly ? (
            <MentionContext fragment={fragment} pinpoint={pinpoint} navigate={navigate} />
          ) : (
            <>
              {phase === 'loading' && <div className="ed-muted">Loading evidence…</div>}
              {phase === 'error' && <div className="bulk-popover-error">{err}</div>}
              {phase === 'ready' && (
                <EvidenceBody
                  evidence={ev}
                  pathname={pathname}
                  compact
                  omitDocId={fragment.document_id}
                  mentionFallback={
                    <MentionContext fragment={fragment} pinpoint={pinpoint} navigate={navigate} />
                  }
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* References — every place this person appears, one row per document. The single
 * "fix this grouping" surface: see each reference (with how it got here:
 * auto-matched vs confirmed), and move out any that are actually someone else.
 * Replaces the old Split + "how this was assembled" panels. One atomic POST
 * /split moves the selected references into their own person — no orphan window. */
/* Given-name parsing for the reference pills — a client mirror of the backend
 * name veto (witness_resolution/name_gate.py): comma forms and title-case
 * Western order are reliable; all-caps no-comma is order-ambiguous → null;
 * titles/roles never count as given names. Used to flag references whose first
 * name can't be this person (the one-click carve filter). */
const REF_NAME_STOP = new Set([
  'the', 'a', 'an', 'mr', 'mrs', 'ms', 'miss', 'dr', 'officer', 'ofc',
  'detective', 'det', 'deputy', 'dep', 'sheriff', 'sergeant', 'sgt',
  'lieutenant', 'lt', 'captain', 'capt', 'trooper', 'agent', 'investigator',
  'chief', 'judge', 'attorney', 'atty', 'defendant', 'victim', 'witness',
  'suspect', 'jr', 'sr', 'ii', 'iii', 'iv', 'aka', 'unknown', 'unk', 'doe',
]);
function refGivenName(name) {
  if (!name) return null;
  const raw = String(name);
  const toks = (s) => (s.match(/[A-Za-z][A-Za-z'’-]*/g) || [])
    .map((t) => t.toLowerCase()).filter((t) => !REF_NAME_STOP.has(t));
  if (raw.includes(',')) {
    return toks(raw.split(',').slice(1).join(' ')).find((t) => t.length >= 2) || null;
  }
  const letters = raw.replace(/[^A-Za-z]/g, '');
  const allCaps = letters.length > 0 && letters === letters.toUpperCase();
  const t = toks(raw);
  if (t.length >= 2 && !allCaps && t[0].length >= 2) return t[0];
  return null; // single token / all-caps order-ambiguous → no reliable given name
}
function givensCompatible(a, b) {
  return a === b || a.slice(0, 3) === b.slice(0, 3)
    || a.startsWith(b) || b.startsWith(a);
}

function ReferencesSection({ caseId, witness, onSplit, onMoveRefs, moveTargets, busy, pathname, onChanged }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | loading | ready | error
  const [fragments, setFragments] = useState([]);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [splitting, setSplitting] = useState(false);
  const [moving, setMoving] = useState(false);
  // E2 — ✂ Not this person: one-click ejection. Routes through the SPLIT
  // machinery (binding-level move + the mirror's durable NOT_SAME carve, so
  // the pair can never be auto-refolded) rather than the log-only separate
  // endpoint — separate silently no-opped on cards whose grouping had no
  // live SAME_AS to retract (2026-07-20, Clinton/Richardson: three 200s,
  // zero visible effect). Split is the path the bulk "Move out" button uses
  // and is proven to move the row every time.
  const [separatingId, setSeparatingId] = useState(null);
  const doSeparate = useCallback(async (entityId) => {
    setSeparatingId(entityId); setError('');
    try {
      await api.post(`/cases/${caseId}/witnesses/${witness.id}/split`, {
        entity_ids: [entityId],
      });
      onChanged?.();
    } catch (e) {
      setError(e.message || 'Could not move this reference out.');
    } finally {
      setSeparatingId(null);
    }
  }, [caseId, witness.id, onChanged]);
  const [movePickerRect, setMovePickerRect] = useState(null);
  const [query, setQuery] = useState('');
  const [prov, setProv] = useState('all'); // all | auto | confirmed
  const [depth, setDepth] = useState('all'); // all | stmt (has statements) | mention (mention only)
  // Go deep on this person (Phase 3, flag-gated): Arc reads each unread
  // mention-only document and promotes quote-verified statements — or stamps
  // "read, nothing substantive". Metered; bounded per click (server cap).
  const { profile } = useUserProfile();
  const goDeepOn = !!profile?.features?.witness_go_deep;
  const [deepRunning, setDeepRunning] = useState(false);
  const [deepNote, setDeepNote] = useState('');
  const unreadMentions = fragments.filter(
    (f) => (f.evidence_count || 0) === 0 && !f.deep_read,
  ).length;
  const runGoDeep = useCallback(async () => {
    setDeepRunning(true); setError(''); setDeepNote('');
    try {
      const out = await api.post(
        `/cases/${caseId}/witnesses/${witness.id}/go-deep`, {},
      );
      const read = (out.reads || []).length;
      setDeepNote(
        `Read ${read} document${read === 1 ? '' : 's'} — `
        + `${out.promoted_total} statement${out.promoted_total === 1 ? '' : 's'} promoted`
        + (out.skipped ? ` · ${out.skipped} more to read` : '')
        + (out.budget_exhausted
          ? ' · AI spending cap reached — remaining docs resume when the cap lifts'
          : ''),
      );
      // Refetch the rows in place: go-deep changes evidence/stamps, NOT
      // membership, so the membership re-arm effect never fires — without
      // this the section keeps stale "mention only" rows and a stale
      // unread count on the button (user-hit 2026-07-21).
      try {
        const d = await api.get(`/cases/${caseId}/witnesses/${witness.id}/members`);
        setFragments(d.fragments || []);
        setSelected(new Set());
      } catch { /* roster reload below still refreshes counts */ }
      onChanged?.();
    } catch (e) {
      setError(e.message || 'Go deep failed.');
    } finally {
      setDeepRunning(false);
    }
  }, [caseId, witness.id, onChanged]);
  // Guards a single load per open. NOT a `phase` dependency: setting phase inside
  // the effect must not re-run it (the re-run's cleanup would flip `cancelled`
  // and discard the in-flight response, hanging on "Loading…" even on a 200).
  const loadedRef = useRef(false);

  // Membership changed under us (a Gather folded references in, a Split moved
  // them out) → the loaded list is stale. Re-arm the loader so the open section
  // refetches; otherwise the header count (member_entity_ids) and the rows
  // silently diverge. Stale selections are cleared with it.
  // Name pills (cleanup rail): filter by exact reference name, plus the smart
  // "different first name" pill. Multi-select; composes with search + provenance.
  const [nameKeys, setNameKeys] = useState(() => new Set());
  const [conflictOnly, setConflictOnly] = useState(false);
  const [namesExpanded, setNamesExpanded] = useState(false);

  const membershipVersion = witness.member_entity_ids?.length ?? 0;
  useEffect(() => {
    loadedRef.current = false;
    setSelected(new Set());
    setNameKeys(new Set()); setConflictOnly(false); setNamesExpanded(false);
    // Search + provenance are per-card work state too — carrying them to
    // the next card silently hides its references (worst on ≤1-fragment
    // cards, where the filter UI isn't even rendered to clear them).
    setQuery(''); setProv('all'); setDepth('all');
  }, [membershipVersion, witness.id]);

  // Load the bound fragments when the section is open and the loader is armed.
  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    let cancelled = false;
    (async () => {
      setPhase('loading'); setError('');
      try {
        const d = await api.get(`/cases/${caseId}/witnesses/${witness.id}/members`);
        if (cancelled) return;
        if (d.timing) console.log('[timing:references]', witness.display_name, d.timing);
        setFragments(d.fragments || []);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Could not load fragments.');
        setPhase('error');
        loadedRef.current = false; // allow a retry on reopen
      }
    })();
    return () => { cancelled = true; };
  }, [open, caseId, witness.id, membershipVersion]);

  const toggle = (eid) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(eid)) next.delete(eid); else next.add(eid);
    return next;
  });

  // Guard mirrors the backend: peel a non-empty SUBSET (leave ≥1 behind).
  const count = selected.size;
  const canSplit = count > 0 && count < fragments.length && !busy && !splitting && !moving;
  const canMove = canSplit && (moveTargets || []).length > 0 && !!onMoveRefs;

  // ── Filter + search for bulk cleanup ──
  // The whole reason to filter here is to isolate a mis-grouped name (e.g. every
  // "Justin" reference folded into Dylan) or the unreviewed auto-matches, then
  // select the whole shown set in one click and move them out. Anything not
  // explicitly confirmed counts as auto (unreviewed) so the two chips partition
  // the list and their counts always sum to All. Selection persists across
  // filter changes so you can accumulate a set across several searches.
  const provOf = (f) => (f.provenance === 'confirmed' ? 'confirmed' : 'auto');
  const provCounts = useMemo(() => {
    const confirmed = fragments.filter((f) => provOf(f) === 'confirmed').length;
    return { all: fragments.length, confirmed, auto: fragments.length - confirmed };
  }, [fragments]);
  // Name groups for the pill rail: unique reference names (case-insensitive,
  // keep the first surface form), counted, heaviest first.
  const nameGroups = useMemo(() => {
    const m = new Map();
    for (const f of fragments) {
      const key = (f.name || 'unnamed').trim().toLowerCase();
      const g = m.get(key);
      if (g) g.count += 1;
      else m.set(key, { key, label: (f.name || 'unnamed').trim(), count: 1 });
    }
    return [...m.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [fragments]);

  // The person's given name = the majority given name across references. A
  // reference whose OWN given name is incompatible with it can't be this
  // person — the "⚠ Different first name" pill isolates them all at once,
  // immune to shared surnames and filename matches.
  const conflictIds = useMemo(() => {
    const tally = new Map();
    const givenOf = new Map();
    for (const f of fragments) {
      const g = refGivenName(f.name);
      givenOf.set(f.entity_id, g);
      if (g) tally.set(g, (tally.get(g) || 0) + 1);
    }
    let majority = null; let best = 0;
    for (const [g, n] of tally) if (n > best) { majority = g; best = n; }
    if (!majority) return new Set();
    const out = new Set();
    for (const f of fragments) {
      const g = givenOf.get(f.entity_id);
      if (g && !givensCompatible(g, majority)) out.add(f.entity_id);
    }
    return out;
  }, [fragments]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return fragments.filter((f) => {
      if (prov !== 'all' && provOf(f) !== prov) return false;
      if (depth === 'stmt' && (f.evidence_count || 0) === 0) return false;
      if (depth === 'mention' && (f.evidence_count || 0) > 0) return false;
      if (conflictOnly && !conflictIds.has(f.entity_id)) return false;
      if (nameKeys.size > 0
          && !nameKeys.has((f.name || 'unnamed').trim().toLowerCase())) return false;
      if (!needle) return true;
      return (f.name || '').toLowerCase().includes(needle)
        || (f.filename || '').toLowerCase().includes(needle);
    });
  }, [fragments, query, prov, depth, nameKeys, conflictOnly, conflictIds]);

  // Inventory: how many references carry statements vs are mentions only —
  // the honest "is this card substantive?" line, and the depth filter counts.
  const depthCounts = useMemo(() => {
    const stmt = fragments.filter((f) => (f.evidence_count || 0) > 0).length;
    const statements = fragments.reduce((n, f) => n + (f.evidence_count || 0), 0);
    return { stmt, mention: fragments.length - stmt, statements };
  }, [fragments]);

  // Auto-open: statement rows open by default when there are few enough that
  // their evidence fetches stay cheap; mention-only rows are free to open
  // (snippet ships with the fragment) so they open whenever the list is
  // small enough to read comfortably.
  const autoOpenIds = useMemo(() => {
    const out = new Set();
    if (depthCounts.stmt <= 4) {
      fragments.forEach((f) => { if ((f.evidence_count || 0) > 0) out.add(f.entity_id); });
    }
    if (fragments.length <= 8) {
      fragments.forEach((f) => { if ((f.evidence_count || 0) === 0) out.add(f.entity_id); });
    }
    return out;
  }, [fragments, depthCounts]);
  const shownIds = useMemo(() => filtered.map((f) => f.entity_id), [filtered]);
  const allShownSelected = shownIds.length > 0 && shownIds.every((id) => selected.has(id));
  const toggleAllShown = () => setSelected((s) => {
    const next = new Set(s);
    if (allShownSelected) shownIds.forEach((id) => next.delete(id));
    else shownIds.forEach((id) => next.add(id));
    return next;
  });
  const hasTools = fragments.length > 1;

  const doSplit = async () => {
    setSplitting(true); setError('');
    try {
      await onSplit(witness, [...selected]);
      // Parent reloads + selects the new witness → this panel remounts.
    } catch (e) {
      setError(e.message || 'Split failed.');
      setSplitting(false);
    }
  };

  // Move the ticked references into an EXISTING person (picked from the same
  // roster pool as "Merge into…"). Focus stays here — the user is mid-cleanup
  // on this card; the membershipVersion effect refetches the shrunken list.
  const doMove = async (target) => {
    setMovePickerRect(null);
    setMoving(true); setError('');
    try {
      await onMoveRefs(witness, [...selected], target.id);
      setMoving(false);
    } catch (e) {
      setError(e.message || 'Move failed.');
      setMoving(false);
    }
  };

  return (
    <div className="wt-split">
      <button
        type="button"
        className="wt-split-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="wt-split-caret">{open ? '▾' : '▸'}</span>
        References — where this person appears ({witness.member_entity_ids?.length || 0})
      </button>
      {open && (
        <div className="wt-split-body">
          {phase === 'loading' && <div className="ed-muted">Loading references…</div>}
          {phase === 'error' && <div className="bulk-popover-error">{error}</div>}
          {phase === 'ready' && (
            <>
              <div className="ed-muted wt-split-hint">
                {hasTools ? (
                  <>Each row is this person as referenced in one document, with the
                  statements extracted there. Tick any that are actually a{' '}
                  <b>different person</b>, then move them out into their own
                  record — or into a person who already exists. At least one must stay here.</>
                ) : (
                  <>This person is referenced in one document so far — Gather can look for more.</>
                )}
              </div>
              <div className="ed-muted wt-ref-inventory">
                {fragments.length} document{fragments.length === 1 ? '' : 's'}
                {' · '}{depthCounts.statements} statement{depthCounts.statements === 1 ? '' : 's'}
                {depthCounts.mention > 0 && <>{' · '}{depthCounts.mention} mention{depthCounts.mention === 1 ? '' : 's'} only</>}
              </div>

              {goDeepOn && (unreadMentions > 0 || deepNote) && (
                <div className="wt-ref-godeep" style={{ marginTop: 6 }}>
                  {unreadMentions > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={deepRunning || busy}
                      onClick={runGoDeep}
                      title="Arc reads each mention-only document for this person and adds quote-verified statements — or marks it read with nothing substantive."
                    >
                      {deepRunning
                        ? 'Reading…'
                        : `🔎 Go deep on this person (${unreadMentions} unread mention${unreadMentions === 1 ? '' : 's'})`}
                    </button>
                  )}
                  {deepNote && <span className="ed-muted" style={{ marginLeft: 8 }}>{deepNote}</span>}
                </div>
              )}

              {hasTools && (
                <>
                  <div className="wt-ref-tools">
                    <div className="wt-sug-search wt-ref-search">
                      <input
                        type="text"
                        className="input wt-sug-search-input"
                        placeholder="Search name or document…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                      {query && (
                        <button
                          type="button"
                          className="wt-sug-search-clear"
                          onClick={() => setQuery('')}
                          aria-label="Clear search"
                        >✕</button>
                      )}
                    </div>
                    <div className="wt-ref-prov">
                      {[
                        { key: 'all', label: 'All', n: provCounts.all },
                        { key: 'auto', label: 'Auto-matched', n: provCounts.auto },
                        { key: 'confirmed', label: 'Confirmed', n: provCounts.confirmed },
                      ].map((c) => (
                        <button
                          key={c.key}
                          type="button"
                          className={`wt-rail-chip ${prov === c.key ? 'wt-rail-chip-on' : ''}`}
                          onClick={() => setProv(c.key)}
                        >
                          <span>{c.label}</span>
                          <span className="wt-rail-count">{c.n}</span>
                        </button>
                      ))}
                    </div>
                    <div className="wt-ref-prov">
                      {[
                        { key: 'stmt', label: 'With statements', n: depthCounts.stmt },
                        { key: 'mention', label: 'Mentions only', n: depthCounts.mention },
                      ].map((c) => (
                        <button
                          key={c.key}
                          type="button"
                          className={`wt-rail-chip ${depth === c.key ? 'wt-rail-chip-on' : ''}`}
                          onClick={() => setDepth((d) => (d === c.key ? 'all' : c.key))}
                        >
                          <span>{c.label}</span>
                          <span className="wt-rail-count">{c.n}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {nameGroups.length > 1 && (
                    <div className="wt-ref-names">
                      {conflictIds.size > 0 && (
                        <button
                          type="button"
                          className={`wt-rail-chip wt-ref-conflict-chip ${conflictOnly ? 'wt-rail-chip-on' : ''}`}
                          title="References whose first name can't be this person — the likeliest carve-outs, immune to shared surnames and file names"
                          onClick={() => setConflictOnly((v) => !v)}
                        >
                          <span>⚠ Different first name</span>
                          <span className="wt-rail-count">{conflictIds.size}</span>
                        </button>
                      )}
                      {(namesExpanded ? nameGroups : nameGroups.slice(0, 12)).map((g) => (
                        <button
                          key={g.key}
                          type="button"
                          className={`wt-rail-chip ${nameKeys.has(g.key) ? 'wt-rail-chip-on' : ''}`}
                          title={`Show only references named “${g.label}”`}
                          onClick={() => setNameKeys((s) => {
                            const next = new Set(s);
                            if (next.has(g.key)) next.delete(g.key); else next.add(g.key);
                            return next;
                          })}
                        >
                          <span>{g.label}</span>
                          <span className="wt-rail-count">{g.count}</span>
                        </button>
                      ))}
                      {nameGroups.length > 12 && (
                        <button
                          type="button"
                          className="wt-linkish"
                          onClick={() => setNamesExpanded((v) => !v)}
                        >
                          {namesExpanded ? 'Fewer names' : `+${nameGroups.length - 12} more names`}
                        </button>
                      )}
                      {(nameKeys.size > 0 || conflictOnly) && (
                        <button
                          type="button"
                          className="wt-linkish"
                          onClick={() => { setNameKeys(new Set()); setConflictOnly(false); }}
                        >Clear name filter</button>
                      )}
                    </div>
                  )}
                  <div className="wt-ref-selbar">
                    <button
                      type="button"
                      className="wt-linkish"
                      onClick={toggleAllShown}
                      disabled={shownIds.length === 0}
                    >
                      {allShownSelected ? 'Deselect shown' : `Select all shown (${shownIds.length})`}
                    </button>
                    {count > 0 && (
                      <button
                        type="button"
                        className="wt-linkish"
                        onClick={() => setSelected(new Set())}
                      >Clear selection ({count})</button>
                    )}
                    <span className="wt-ref-count">Showing {filtered.length} of {fragments.length}</span>
                  </div>
                </>
              )}

              <div className="wt-frag-list">
                {filtered.length === 0 && (
                  <div className="ed-muted">
                    {fragments.length === 0
                      ? 'No references bound yet — Gather can find them.'
                      : (
                        <>
                          No references match this filter.{' '}
                          <button
                            type="button"
                            className="wt-linkish"
                            onClick={() => {
                              setQuery(''); setProv('all'); setDepth('all');
                              setNameKeys(new Set()); setConflictOnly(false);
                            }}
                          >Clear filters</button>
                        </>
                      )}
                  </div>
                )}
                {filtered.map((f) => (
                  <ReferenceRow
                    key={f.entity_id}
                    caseId={caseId}
                    witnessId={witness.id}
                    fragment={f}
                    selected={selected.has(f.entity_id)}
                    onToggleSelect={() => toggle(f.entity_id)}
                    pathname={pathname}
                    onSeparate={fragments.length > 1 ? doSeparate : null}
                    separating={separatingId === f.entity_id}
                    defaultOpen={autoOpenIds.has(f.entity_id)}
                  />
                ))}
              </div>
              {error && <div className="bulk-popover-error">{error}</div>}
              {hasTools && (
              <div className="wt-card-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={doSplit}
                  disabled={!canSplit}
                  title={
                    count === 0 ? 'Tick the references that are a different person'
                      : count >= fragments.length ? 'Leave at least one reference here'
                        : `Move ${count} reference${count > 1 ? 's' : ''} out — each distinct name becomes its own person`
                  }
                >
                  {splitting ? 'Moving…' : `Move out ${count || ''}`.trim()} — these are someone else
                </button>
                {onMoveRefs && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={(e) => setMovePickerRect(
                      movePickerRect ? null : e.currentTarget.getBoundingClientRect(),
                    )}
                    disabled={!canMove}
                    title={
                      count === 0 ? 'Tick the references that belong to an existing person'
                        : count >= fragments.length ? 'Leave at least one reference here — to combine whole people, use Merge into…'
                          : (moveTargets || []).length === 0 ? 'No other people on the roster yet'
                            : `Move ${count} reference${count > 1 ? 's' : ''} into a person who already exists`
                    }
                  >
                    {moving ? 'Moving…' : `⇄ Move ${count || ''} to existing person…`.replace('  ', ' ')}
                  </button>
                )}
                {movePickerRect && (
                  <MergePicker
                    rect={movePickerRect}
                    targets={moveTargets || []}
                    title={`Move ${count} reference${count > 1 ? 's' : ''} into…`}
                    onClose={() => setMovePickerRect(null)}
                    onPick={doMove}
                  />
                )}
              </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* Evidence rail + item list, rendered FROM a bucketized evidence object
 * (`{registry, counts, buckets}`). Shared by the witness DetailPanel and the
 * per-fragment split exploration so both read identically. Each instance owns
 * its own active bucket. */
function EvidenceBody({ evidence, pathname, compact = false, omitDocId = null, excludeBuckets = null, collapsible = false, title = 'Extracted statements & events', mentionFallback = null }) {
  const [bucket, setBucket] = useState('all');
  // Collapsed by default in the person panel: the Arc summary + References carry
  // the story, and the raw statement/event rail is a one-click-away drill-down
  // rather than a wall of text on open. Ignored unless `collapsible` is set.
  const [open, setOpen] = useState(false);
  // Drop whole buckets a caller doesn't want here (e.g. the person panel hides
  // the bare "Reports & documents" bucket because the References section below
  // is the canonical, richer list of the same documents — no double-listing).
  const registry = useMemo(() => {
    const reg = evidence?.registry || [];
    if (!excludeBuckets || excludeBuckets.length === 0) return reg;
    return reg.filter((r) => !excludeBuckets.includes(r.key));
  }, [evidence, excludeBuckets]);
  const rawBuckets = evidence?.buckets || {};

  // Per-reference expansion: the row header already links the reference's own
  // document, so a bare "document" item pointing back at that same file is pure
  // repetition. Drop it and recompute counts so the rail chips stay honest.
  const buckets = useMemo(() => {
    if (!omitDocId) return rawBuckets;
    const out = {};
    for (const [k, list] of Object.entries(rawBuckets)) {
      out[k] = (list || []).filter(
        (it) => !(it.kind === 'document' && it.document_id === omitDocId),
      );
    }
    return out;
  }, [rawBuckets, omitDocId]);

  const counts = useMemo(() => {
    if (!omitDocId) return evidence?.counts || {};
    return Object.fromEntries(
      Object.entries(buckets).map(([k, list]) => [k, list.length]),
    );
  }, [buckets, omitDocId, evidence]);

  const rail = useMemo(() => {
    const total = registry.reduce((sum, r) => sum + (counts[r.key] || 0), 0);
    return [
      { key: 'all', label: 'All', icon: 'all', count: total },
      ...registry.map((r) => ({ ...r, count: counts[r.key] || 0 })),
    ];
  }, [registry, counts]);

  const items = useMemo(() => {
    if (bucket === 'all') return registry.flatMap((r) => buckets[r.key] || []);
    return buckets[bucket] || [];
  }, [bucket, registry, buckets]);

  // When the reference's only evidence was the source document itself (now
  // omitted as a repeat of the header link), skip the empty rail entirely and
  // point the user back at the document link above.
  const grandTotal = rail.find((r) => r.key === 'all')?.count || 0;
  if (omitDocId && grandTotal === 0) {
    // Mention-only reference: the person is referenced here but no
    // statements were extracted. Show the mention in context (passed by the
    // reference row) instead of a dead end.
    return mentionFallback || (
      <div className="ed-muted">
        Referenced in this document, but no statements were extracted — open
        the document above to read the mention.
      </div>
    );
  }
  // Person panel with buckets excluded (documents live in the References section
  // below): if nothing substantive remains, render nothing rather than an empty
  // rail — References carries the whole story.
  if (excludeBuckets && excludeBuckets.length > 0 && grandTotal === 0) {
    return null;
  }

  const body = (
    <>
      <div className={`wt-rail ${compact ? 'wt-rail-compact' : ''}`}>
        {rail.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`wt-rail-chip ${bucket === r.key ? 'wt-rail-chip-on' : ''}`}
            onClick={() => setBucket(r.key)}
          >
            <span>{evidenceGlyph(r.icon)} {r.label}</span>
            <span className="wt-rail-count">{r.count}</span>
          </button>
        ))}
      </div>
      <div className="wt-ev-list">
        {items.length === 0 && <div className="ed-muted">No evidence in this category.</div>}
        {items.map((item, idx) => (
          <EvidenceItem
            key={item.claim_id || item.event_id || item.document_id || idx}
            item={item}
            pathname={pathname}
          />
        ))}
      </div>
    </>
  );

  if (!collapsible) return body;

  // Collapsible person-panel form: same toggle affordance as the References
  // section below, default closed, count in the header so it reads at a glance.
  return (
    <div className="wt-split">
      <button
        type="button"
        className="wt-split-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="wt-split-caret">{open ? '▾' : '▸'}</span>
        {title} ({grandTotal})
      </button>
      {open && <div className="wt-split-body">{body}</div>}
    </div>
  );
}

/* Arc-generated, citation-grounded "make sense of this person" summary. Each
 * point cites real graph evidence items → click a citation to open its source
 * document. "View in graph" NAVIGATES to the `?graph=w:<id>` deep link
 * (features.graph_explorer) — a real history entry, so the browser Back
 * button returns to this card (GraphDeepLink owns the overlay). With the
 * flag off it falls back to the text dossier modal, which needs the
 * summary's entity id first. Read-only, on-demand (no auto-spend per
 * witness click). */
/* C1 coherence pass: the person's adjudication story on their own card —
 * conflict banner, pending Arc proposal (confirm/reject), confirmed ruling.
 * Same shared cards as the entity dossier modal, but keyed to the CURATED
 * witness (bound fragments + same-name fragments folded as one) via
 * /role-profile — so the Witnesses tab and the dossier tell one story. */
function WitnessRoleSection({ caseId, witness }) {
  const [profile, setProfile] = useState(null);
  const load = useCallback(async () => {
    try {
      const d = await api.get(`/cases/${caseId}/witnesses/${witness.id}/role-profile`);
      setProfile(d?.role_profile || null);
    } catch {
      setProfile(null);
    }
  }, [caseId, witness.id]);
  useEffect(() => { load(); }, [load]);

  if (!profile) return null;
  // Compatible multi-role reads (Officer + Witness) still render — as the
  // quiet "roles are compatible" note inside RoleProfileCards, never an
  // alarm. Hiding the section entirely would leave the differing reads
  // invisible, which is its own kind of incoherence.
  const multiRole = Object.keys(profile.buckets || {}).length >= 2;
  if (!profile.conflict && !profile.adjudication
      && !profile.pending_adjudication && !multiRole) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <RoleProfileCards
        caseId={caseId}
        profile={profile}
        entityName={witness.display_name}
        onChanged={load}
      />
    </div>
  );
}

function WitnessSummarySection({ caseId, witness, pathname, reloadKey }) {
  // Summaries PERSIST: the card reloads the saved current version for free on
  // open (GET — no LLM spend); only "Summarize/Update" runs (and meters) a
  // generation, which appends a new version to the archive.
  const [phase, setPhase] = useState('init'); // init | idle | loading | ready | error
  const [summary, setSummary] = useState(null); // what's displayed (current OR archived)
  const [current, setCurrent] = useState(null); // the latest persisted version
  const [versions, setVersions] = useState(null); // archive list, fetched on first open
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [error, setError] = useState('');
  const [graphOpen, setGraphOpen] = useState(false);
  const navigate = useNavigate();
  const { profile } = useUserProfile();
  const graphExplorerOn = !!profile?.features?.graph_explorer;
  const openGraph = () => navigate(`${pathname}?graph=w:${witness.id}`);

  // Auto-refresh: summaries are correction-aware (they defer to confirmed
  // corrections), so a stale one regenerates itself — once per card open,
  // never in a loop; a failed generation falls back to the saved version
  // with the stale banner (and the manual Update button) intact.
  const autoRefreshedRef = useRef(null);
  const [autoKick, setAutoKick] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPhase('init'); setSummary(null); setCurrent(null); setVersions(null);
    setVersionsOpen(false); setError(''); setGraphOpen(false);
    (async () => {
      try {
        const d = await api.get(`/cases/${caseId}/witnesses/${witness.id}/summary`);
        if (cancelled) return;
        if (d?.summary) {
          setSummary(d.summary); setCurrent(d.summary); setPhase('ready');
          if (d.summary.stale) setAutoKick(true);
        }
        else setPhase('idle');
      } catch {
        // Persisted-summary load is best-effort — fall back to the generate CTA.
        if (!cancelled) setPhase('idle');
      }
    })();
    return () => { cancelled = true; };
    // reloadKey: a workbench action or go-deep run changed the evidence under
    // us — refetch so the stale banner (and the once-per-open auto-refresh,
    // if it hasn't spent yet) reacts without a card close/reopen.
  }, [caseId, witness.id, reloadKey]);

  useEffect(() => {
    if (!autoKick) return;
    setAutoKick(false);
    if (autoRefreshedRef.current === witness.id) return;
    autoRefreshedRef.current = witness.id;
    generate();
  }, [autoKick]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setPhase('loading'); setError(''); setVersionsOpen(false);
    try {
      const d = await api.post(`/cases/${caseId}/witnesses/${witness.id}/summary`);
      setSummary(d); setCurrent(d); setVersions(null); setPhase('ready');
    } catch (e) {
      setError(e.message || 'Could not summarize the evidence.');
      // Keep showing the saved summary if we have one — the spend just failed.
      setPhase(current ? 'ready' : 'error');
      if (current) setSummary(current);
    }
  };

  const toggleVersions = async () => {
    const opening = !versionsOpen;
    setVersionsOpen(opening);
    if (opening && versions == null) {
      try {
        const d = await api.get(`/cases/${caseId}/witnesses/${witness.id}/summary/versions`);
        setVersions(d?.versions || []);
      } catch { setVersions([]); }
    }
  };

  const viewVersion = async (vid) => {
    setVersionsOpen(false);
    if (current && vid === current.summary_id) { setSummary(current); return; }
    try {
      const d = await api.get(`/cases/${caseId}/witnesses/${witness.id}/summary/versions/${vid}`);
      setSummary(d);
    } catch (e) {
      setError(e.message || 'Could not load that version.');
    }
  };

  const viewingArchived = !!(summary && current && summary.summary_id !== current.summary_id);
  const stale = !viewingArchived ? summary?.stale : null;
  const versionCount = current?.version_count || 0;
  const fmtWhen = (s) => (s ? new Date(s).toLocaleString() : '');
  const entity = summary?.entity;
  const entityId = entity?.id || entity?.entity_id;

  return (
    <div className="wt-summary">
      <div className="wt-summary-head">
        <span className="wt-summary-title">✨ Evidence summary</span>
        <span className="wt-summary-controls">
          {graphExplorerOn && (
            <button
              className="btn btn-sm"
              onClick={openGraph}
              title="Browse this person's connections on the graph canvas"
            >🕸 Graph</button>
          )}
          {phase === 'idle' && (
            <button className="btn btn-sm btn-primary" onClick={generate}>Summarize with Arc</button>
          )}
          {phase === 'loading' && <span className="ed-muted">Arc is reading the evidence…</span>}
          {phase === 'ready' && versionCount > 1 && (
            <span className="wt-summary-versions-wrap">
              <button className="btn btn-sm" onClick={toggleVersions} title="Browse earlier versions">
                🗂 {versionCount} versions
              </button>
              {versionsOpen && (
                <div className="wt-summary-versions">
                  {versions == null && <div className="ed-muted wt-summary-version-row">Loading…</div>}
                  {(versions || []).map((v) => (
                    <button
                      key={v.summary_id}
                      className={`wt-summary-version-row${summary?.summary_id === v.summary_id ? ' is-viewing' : ''}`}
                      onClick={() => viewVersion(v.summary_id)}
                    >
                      <span className="wt-summary-version-when">
                        {fmtWhen(v.created_at)}
                        {current?.summary_id === v.summary_id ? ' · current' : ''}
                      </span>
                      <span className="wt-summary-version-headline">{v.headline}</span>
                    </button>
                  ))}
                </div>
              )}
            </span>
          )}
          {phase === 'ready' && (
            <button className="btn btn-sm" onClick={generate} title="Regenerate — saves as a new version">↻ Update</button>
          )}
          {phase === 'error' && (
            <button className="btn btn-sm" onClick={generate}>Retry</button>
          )}
        </span>
      </div>

      {phase === 'idle' && (
        <div className="wt-summary-idle">
          Arc reads everywhere this person appears and lays out what the documents
          say about them — each point links straight to its source. Generated on
          demand, then saved with this person.
        </div>
      )}

      {error && <div className="bulk-popover-error">{error}</div>}

      {phase === 'ready' && summary && viewingArchived && (
        <div className="wt-summary-archived">
          Archived version from {fmtWhen(summary.created_at)}
          <button className="btn btn-sm" onClick={() => setSummary(current)}>Back to current</button>
        </div>
      )}

      {phase === 'ready' && summary && stale && (
        <div className="wt-summary-stale">
          Evidence has changed since this summary
          {stale.evidence_count_then != null && stale.evidence_count_now != null
            ? ` (${stale.evidence_count_then} → ${stale.evidence_count_now} items)`
            : stale.deep_read_changed ? ' (new statements were promoted)'
            : stale.members_changed ? " (this person's grouping changed)" : ''}
          <button className="btn btn-sm" onClick={generate}>↻ Update</button>
        </div>
      )}

      {phase === 'ready' && summary && (
        <div className="wt-summary-body">
          {summary.headline && <div className="wt-summary-headline">{summary.headline}</div>}
          {summary.created_at && (
            <div className="wt-summary-meta">
              Generated {fmtWhen(summary.created_at)}
              {summary.evidence_count != null ? ` · based on ${summary.evidence_count} evidence items` : ''}
            </div>
          )}
          {summary.points?.length > 0 ? (
            <ul className="wt-summary-points">
              {summary.points.map((p, i) => (
                <li key={i} className="wt-summary-point">
                  <span className="wt-summary-text">{p.text}</span>
                  {p.citations?.length > 0 && (
                    <span className="wt-summary-cites">
                      {dedupeCitationsByFamily(p.citations).map((c, j) => <CitationLink key={j} item={c} pathname={pathname} />)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="ed-muted">No evidence is linked to this person yet.</div>
          )}
          {summary.gaps?.length > 0 && (
            <div className="wt-summary-gaps">
              <div className="wt-summary-gaps-title">Open questions</div>
              <ul>{summary.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
            </div>
          )}
          {(entityId || graphExplorerOn) && (
            <button
              className="btn btn-sm wt-summary-graph"
              onClick={graphExplorerOn ? openGraph : () => setGraphOpen(true)}
            >
              🕸 View {summary.display_name} in the graph
            </button>
          )}
        </div>
      )}

      {graphOpen && !graphExplorerOn && entityId && (
        <EntityDossierModal
          caseId={caseId}
          entityId={entityId}
          name={summary.display_name}
          entityType={entity?.entity_type}
          onClose={() => setGraphOpen(false)}
        />
      )}
    </div>
  );
}

/* Pick which name variant is this witness's canonical display name. Candidates =
   the display name + every alias (the names folded-in fragments are known by);
   picking one swaps it to canonical and keeps the rest as searchable aliases.
   Only shows when there's more than one name to choose from. */
function NamePicker({ witness, busy, onPick }) {
  const [open, setOpen] = useState(false);
  const seen = new Set();
  const candidates = [witness.display_name, ...(witness.aliases || [])]
    .map((s) => (s || '').trim())
    .filter((s) => {
      const k = s.toLowerCase();
      if (!s || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  if (candidates.length < 2) return null;
  const pick = (chosen) => {
    setOpen(false);
    if (!chosen || chosen === witness.display_name) return;
    const rest = candidates.filter((n) => n.toLowerCase() !== chosen.toLowerCase());
    onPick(chosen, rest);
  };
  return (
    <div className="wt-namepick">
      <button
        className="btn btn-xs wt-namepick-btn"
        disabled={busy}
        title="Choose which name to display for this person"
        onClick={() => setOpen((o) => !o)}
      >names ▾</button>
      {open && (
        <div className="wt-namepick-menu" role="menu">
          <div className="wt-namepick-hint">Display this person as:</div>
          {candidates.map((n) => (
            <button
              key={n}
              role="menuitemradio"
              aria-checked={n === witness.display_name}
              className={`wt-namepick-opt${n === witness.display_name ? ' is-current' : ''}`}
              onClick={() => pick(n)}
            >
              {n === witness.display_name ? '● ' : '○ '}{n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* Confirm a witness's auto-gathered identity. Shows only when the identity has
   unreviewed AI links; one click records the human audit (its SAME_AS links →
   HUMAN_CONFIRMED) so the ⚠ flag clears. Membership doesn't change. */
function ConfirmIdentityControl({ caseId, witness, busy, onDone }) {
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const ir = witness.identity_review;
  // Confirm applies to accepted people only; a suggested row is curated via
  // Accept/Dismiss, not identity confirmation.
  if (witness.review_state === 'suggested' || ir?.state !== 'unreviewed') return null;
  const confirm = async () => {
    setRunning(true); setErr(null);
    try {
      await api.post(`/cases/${caseId}/witnesses/${witness.id}/confirm-identity`);
      onDone?.();
    } catch (e) {
      setErr(e.message || 'Confirm failed');
    } finally {
      setRunning(false);
    }
  };
  return (
    <button
      className="btn btn-sm wt-confirm-id-btn"
      disabled={busy || running}
      title={err || `Confirm this person was assembled correctly (${ir.unreviewed} unreviewed link${ir.unreviewed === 1 ? '' : 's'})`}
      onClick={confirm}
    >{running ? 'Confirming…' : '✓ Confirm this person'}</button>
  );
}

/* E2 follow-up — Arc's held-back "possible duplicate?" hints: sub-threshold
   arc_held assertions the wave write phase persisted instead of discarding.
   Display + one action (⇄ Merge — the human override the size guard defers
   to). Best-effort: a fetch failure just means no chips. */
function HeldHints({ caseId, witness, onMerge, onOpenWitness, busy, reloadKey }) {
  const [hints, setHints] = useState([]);
  const [dismissing, setDismissing] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get(`/cases/${caseId}/witnesses/${witness.id}/held-hints`);
        if (!cancelled) setHints(d.hints || []);
      } catch { if (!cancelled) setHints([]); }
    })();
    return () => { cancelled = true; };
  }, [caseId, witness.id, reloadKey]);
  const dismiss = async (h) => {
    setDismissing(h.witness_id);
    try {
      await api.post(
        `/cases/${caseId}/witnesses/${witness.id}/held-hints/${h.witness_id}/dismiss`,
      );
      setHints((prev) => prev.filter((x) => x.witness_id !== h.witness_id));
    } catch { /* chip stays; retry available */ } finally {
      setDismissing(null);
    }
  };
  if (!hints.length) return null;
  return (
    <div className="wt-held-hints">
      {hints.map((h) => (
        <div key={h.witness_id} className="wt-held-hint">
          <span style={{ flex: 1 }}>
            🤔 Possible duplicate:{' '}
            {onOpenWitness ? (
              <button
                type="button"
                className="wt-linkish"
                style={{ fontWeight: 600 }}
                title="Open this person's card to compare"
                onClick={() => onOpenWitness(h.witness_id)}
              >{h.witness_name}</button>
            ) : <strong>{h.witness_name}</strong>}
            {h.entities > 1 ? ` (${h.entities} shared trails)` : ''}
            {(h.shared_name || []).length > 0 && (
              <> — both appear as “{(h.shared_name || []).slice(0, 3).join('”, “')}”</>
            )}
            {h.compared
              ? ' — Arc compared both people’s documents and couldn’t confirm. Your call.'
              : ` — the documents haven’t been read yet${h.reason ? ` (${h.reason})` : ''}. Arc will compare them on the next pass.`}
          </span>
          {onMerge && (
            <button
              type="button" className="btn btn-sm"
              disabled={busy || dismissing === h.witness_id}
              onClick={() => onMerge(witness, h.witness_id)}
              title="They're the same — merge this person into them. Reversible from the Recently merged ledger."
            >⇄ Merge</button>
          )}
          <button
            type="button" className="btn btn-sm"
            disabled={busy || dismissing === h.witness_id}
            onClick={() => dismiss(h)}
            title="Not the same person — permanently stop suggesting this pair (and teach the matcher)"
          >{dismissing === h.witness_id ? '…' : '✕ Not the same'}</button>
        </div>
      ))}
    </div>
  );
}

/* The Workbench — the full-width work surface for ONE entity-card. An entity is
   an abstract item layered OVER graph fragments: typed (by derived abstraction),
   identifiable (badges from the layer registry), and freely decomposable (the
   Compose/Separate + Split controls below). Gathers identity + every action
   (review / merge / split / edit / Arc) + evidence + summary in one place.
   (Workbench step W1; plans/WITNESS_WORKBENCH_PLAN.md.) */
function DetailPanel({
  caseId, witness, pathname,
  onPatch, onSplit, onMoveRefs, onRefine, onReview, onMerge, mergeTargets,
  onOpenWitness,
  busy, onChanged, cleanupActive, reloadKey, lintFindings,
}) {
  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [dossier, setDossier] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [pickerRect, setPickerRect] = useState(null);
  const [notSameRect, setNotSameRect] = useState(null);
  const [notSameMsg, setNotSameMsg] = useState(null);
  const [name, setName] = useState(witness.display_name || '');
  const [role, setRole] = useState(witness.role || '');
  const [importance, setImportance] = useState(witness.importance || 'standard');
  const [notes, setNotes] = useState(witness.notes || '');

  // Reload the dossier whenever the selected witness changes OR an action
  // (assemble / separate / split) bumps reloadKey — the witness id is unchanged
  // after an in-place assemble, so without reloadKey the evidence would go stale.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase('loading'); setError(''); setNotSameMsg(null);
      try {
        const d = await api.get(`/cases/${caseId}/witnesses/${witness.id}/dossier`);
        if (cancelled) return;
        if (d.timing) console.log('[timing:evidence]', witness.display_name, d.timing);
        setDossier(d);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Could not load evidence.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, witness.id, reloadKey]);

  // Reset the edit form when switching witnesses.
  useEffect(() => {
    setEditing(false);
    setName(witness.display_name || '');
    setRole(witness.role || '');
    setImportance(witness.importance || 'standard');
    setNotes(witness.notes || '');
  }, [witness.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ev = dossier?.evidence;

  // Split is an identity correction, orthogonal to curation status: available on
  // any active roster item (suggested OR accepted) that binds >1 fragment — there
  // is something to peel apart. Only dismissed items (out of the roster) and
  // single-fragment entities (nothing to split) are excluded. A "witness" is just
  // an accepted entity; merge and split apply the same either way.
  const canSplit = witness.review_state !== 'dismissed'
    && (witness.member_entity_ids?.length || 0) > 1;
  // The References section shows for ANY bound reference — a single-reference
  // person still needs to see where they appear (UI paper-cut #4: the split
  // gate used to hide the whole section, so n=1 cards looked evidence-less).
  const hasRefs = witness.review_state !== 'dismissed'
    && (witness.member_entity_ids?.length || 0) > 0;

  const saveEdit = () => {
    onPatch(witness, {
      display_name: name.trim() || witness.display_name,
      role: role.trim() || null,
      importance,
      notes,
    });
    setEditing(false);
  };

  return (
    <div className="wt-detail">
      <div className="wt-detail-head">
        {editing ? (
          <div className="wt-edit-form">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
            <input className="input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role" />
            <select className="input" value={importance} onChange={(e) => setImportance(e.target.value)}>
              <option value="key">Key</option>
              <option value="standard">Standard</option>
              <option value="peripheral">Peripheral</option>
            </select>
            <textarea className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" />
            <div className="wt-card-actions">
              <button className="btn btn-sm btn-primary" onClick={saveEdit} disabled={busy}>Save</button>
              <button className="btn btn-sm" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="wt-detail-identity">
              <div
                className="wb-abstraction"
                title="How complete this profile is: Single mention (one raw reference) → Partial profile (a few pieces) → Person (many references + role + statements). Derived from what's folded in."
              >
                {ABSTRACTION_LABELS[entityAbstraction(witness)] || entityAbstraction(witness)}
              </div>
              <div className="wt-detail-name-row">
                <h3 className="wt-detail-name">{witness.display_name || 'Unnamed'}</h3>
                <NamePicker witness={witness} busy={busy} onPick={(chosen, rest) => onPatch(witness, { display_name: chosen, aliases: rest })} />
              </div>
              <EntityBadges witness={witness} />
              {witness.aliases?.length > 0 && (
                <div className="wt-detail-aliases">also named as: {witness.aliases.join(', ')}</div>
              )}
              {witness.notes && <div className="wt-detail-notes">📝 {witness.notes}</div>}
            </div>
            <div className="wt-detail-actions">
              <ConfirmIdentityControl caseId={caseId} witness={witness} busy={busy} onDone={onChanged} />
              {witness.review_state === 'suggested' && onReview && (
                <>
                  <button className="btn btn-sm btn-primary" onClick={() => onReview(witness, 'accept')} disabled={busy}>Accept</button>
                  <button className="btn btn-sm" onClick={() => onReview(witness, 'dismiss')} disabled={busy}>Dismiss</button>
                  {witness.deferred
                    ? <button className="btn btn-sm" onClick={() => onReview(witness, 'undefer')} disabled={busy} title="Return to active suggestions">↩ Return</button>
                    : <button className="btn btn-sm" onClick={() => onReview(witness, 'defer')} disabled={busy} title="Decide later">🕓 Defer</button>}
                </>
              )}
              {/* Manual escape hatch — when Assemble missed it and you want to
                  pick the target yourself. Automatic merging lives in Assemble. */}
              {onMerge && (
                <button
                  className="btn btn-sm wt-merge-btn"
                  disabled={busy}
                  title="Manually pick another entity to merge this one into"
                  onClick={(e) => setPickerRect(pickerRect ? null : e.currentTarget.getBoundingClientRect())}
                >⇄ Merge into…</button>
              )}
              {/* The mirror of "Merge into…": record a durable human NOT_SAME
                  between two cards. Knowledge only the human has ("the two
                  Larrys are different people") becomes an unbreakable rule +
                  a gold-set negative — nothing else ever captures it, because
                  no chip asks about pairs the machine doesn't suspect. */}
              <button
                className="btn btn-sm"
                disabled={busy}
                title="Record that this is NOT the same person as another card — they will never be auto-merged, and it counts as a decision"
                onClick={(e) => setNotSameRect(notSameRect ? null : e.currentTarget.getBoundingClientRect())}
              >≠ Not the same as…</button>
              <button className="btn btn-sm" onClick={() => setEditing(true)}>Edit</button>
              {pickerRect && onMerge && (
                <MergePicker
                  rect={pickerRect}
                  targets={mergeTargets || []}
                  onClose={() => setPickerRect(null)}
                  onPick={(t) => { setPickerRect(null); onMerge(witness, t.id); }}
                />
              )}
              {notSameRect && (
                <MergePicker
                  rect={notSameRect}
                  targets={mergeTargets || []}
                  title="≠ Not the same as…"
                  onClose={() => setNotSameRect(null)}
                  onPick={async (t) => {
                    setNotSameRect(null); setNotSameMsg(null);
                    try {
                      await api.post(
                        `/cases/${caseId}/witnesses/${witness.id}/held-hints/${t.id}/dismiss`,
                      );
                      setNotSameMsg({ kind: 'ok', text: `Recorded: not the same person as ${t.display_name || 'that card'}. They will never be auto-merged.` });
                      onChanged?.();
                    } catch (err) {
                      setNotSameMsg({
                        kind: 'err',
                        text: (err.status === 404
                          ? 'Both cards need at least one reference before the separation can be recorded — Gather references first.'
                          : err.message || 'Failed to record.'),
                      });
                    }
                  }}
                />
              )}
            </div>
            {notSameMsg && (
              <div
                className={notSameMsg.kind === 'err' ? 'bulk-popover-error' : 'ed-muted'}
                style={{ fontSize: 12, marginTop: 6 }}
              >{notSameMsg.text}</div>
            )}
          </>
        )}
      </div>

      {/* Per-person "Gather" is GONE (2026-07-06): the background auto-resolve
          loop runs the same calibrated band case-wide, so Gather on a fresh
          roster was a no-op by construction. Its slot is reserved for the
          per-person "Go deep" verb (LLM tier, scoped) once the incremental
          index makes it instant. */}
      <HeldHints caseId={caseId} witness={witness} onMerge={onMerge} onOpenWitness={onOpenWitness} busy={busy} reloadKey={reloadKey} />

      <WitnessRoleSection caseId={caseId} witness={witness} />

      {lintFindings && lintFindings.length > 0 && (
        <div style={{
          marginTop: 12, border: '1px solid rgba(180, 83, 9, 0.45)',
          background: 'rgba(180, 83, 9, 0.08)', borderRadius: 8,
          padding: '10px 12px', fontSize: 13,
        }}>
          <b>⚕ Needs a look ({lintFindings.length})</b>
          {lintFindings.map((f, i) => (
            <div key={i} style={{ marginTop: 6 }}>
              {f.severity === 'error' ? '❗' : '•'} {f.detail}
              {f.other_witness_name && f.other_witness_id !== witness.id && (
                <span style={{ opacity: 0.75 }}> (involves {f.other_witness_name})</span>
              )}
            </div>
          ))}
          <div style={{ marginTop: 6, opacity: 0.75 }}>
            These clear on their own once fixed — use References / Move out /
            Merge above to sort them.
          </div>
        </div>
      )}

      <WitnessSummarySection caseId={caseId} witness={witness} pathname={pathname} reloadKey={reloadKey} />

      {phase === 'loading' && <div className="ed-muted">Loading evidence…</div>}
      {phase === 'error' && <div className="bulk-popover-error">{error}</div>}
      {phase === 'ready' && !hasRefs && (
        // The aggregate statements rail shows ONLY for cards with no bound
        // references (dismissed / unbound): everywhere else the References
        // section is the single unified surface — each reference row carries
        // the statements extracted from that document (or the mention in
        // context when there are none), so a separate cross-document rail
        // was double-listing the same material.
        <EvidenceBody
          evidence={ev}
          pathname={pathname}
          collapsible
        />
      )}

      {hasRefs && (
        <ReferencesSection
          caseId={caseId}
          witness={witness}
          onSplit={onSplit}
          onMoveRefs={onMoveRefs}
          moveTargets={mergeTargets}
          busy={busy}
          pathname={pathname}
          onChanged={onChanged}
        />
      )}

      <PossiblyRelatedSection
        caseId={caseId}
        witness={witness}
        pathname={pathname}
        onChanged={onChanged}
      />
    </div>
  );
}

// Option B fence (user directive 2026-07-17): the card shows everything the
// graph knows about this name, but unvetted name-matches live in a clearly
// separated section — never mixed into the vetted evidence above. Each row is
// a decision: "This is them" binds the reference (a durable confirmed link);
// "Not this person" writes a durable never-again ruling. Both survive
// re-extraction, so the card gets permanently smarter with each call.
function PossiblyRelatedSection({ caseId, witness, pathname, onChanged }) {
  const navigate = useNavigate();
  const [cands, setCands] = useState(null); // null = loading
  const [dropped, setDropped] = useState(0);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const membershipVersion = witness.member_entity_ids?.length ?? 0;
  useEffect(() => {
    let cancelled = false;
    setCands(null); setError('');
    (async () => {
      try {
        const d = await api.get(`/cases/${caseId}/witnesses/${witness.id}/possibly-related`);
        if (cancelled) return;
        setCands(d.candidates || []);
        setDropped(d.dropped || 0);
      } catch {
        if (!cancelled) setCands([]); // fence is best-effort; never block the card
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, witness.id, membershipVersion]);

  const act = async (entityId, kind) => {
    setBusyId(entityId); setError('');
    try {
      if (kind === 'add') {
        await api.post(`/cases/${caseId}/witnesses/${witness.id}/members`, {
          entity_ids: [entityId],
        });
      } else {
        await api.post(`/cases/${caseId}/witnesses/${witness.id}/candidates/${entityId}/not-same`);
      }
      setCands((c) => (c || []).filter((x) => x.entity_id !== entityId));
      onChanged?.();
    } catch (e) {
      setError(e.message || 'Could not save that decision.');
    } finally {
      setBusyId(null);
    }
  };

  if (!cands || cands.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 14, padding: '10px 12px', borderRadius: 8,
        border: '1.5px dashed var(--border, #cbd5e1)',
        background: 'var(--panel-alt, #f8fafc)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 2 }}>
        ？ Possibly this person — your call ({cands.length})
      </div>
      <div className="ed-muted" style={{ marginBottom: 8 }}>
        Name matches found in this case that aren&apos;t linked to this person.
        Nothing here counts as evidence until you add it.
      </div>
      {error && <div className="bulk-popover-error">{error}</div>}
      {cands.map((c) => (
        <div
          key={c.entity_id}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 0', borderTop: '1px solid var(--border, #e2e8f0)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.name || '(unnamed)'}
            </div>
            <div className="ed-muted" style={{ fontSize: 12 }}>
              {(() => {
                // A promote/reject decision needs the source one click away:
                // deep-link the viewer to where this name appears (chunk-
                // granular), same navigate() contract as ReferenceRow —
                // modifier-clicks open a new tab, plain clicks stay in-app.
                const pin = referenceCitation(c, pathname);
                if (!pin) return c.filename ? `in ${c.filename}` : 'source document unknown';
                return (
                  <>
                    {'in '}
                    <a
                      href={pin.href}
                      className="citation-link"
                      title="Open the document where this name appears — check before deciding"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        navigate(pin.href);
                      }}
                    >📄 {c.filename || pin.label}</a>
                  </>
                );
              })()}
              {c.evidence_count > 0 ? ` · ${c.evidence_count} mention${c.evidence_count === 1 ? '' : 's'} in the case graph` : ''}
            </div>
          </div>
          <button
            className="btn btn-sm"
            disabled={busyId === c.entity_id}
            onClick={() => act(c.entity_id, 'add')}
            title="Link this reference to this person (a confirmed decision — it sticks)"
          >
            This is them
          </button>
          <button
            className="btn btn-sm"
            disabled={busyId === c.entity_id}
            onClick={() => act(c.entity_id, 'reject')}
            title="Never suggest this reference for this person again"
          >
            Not this person
          </button>
        </div>
      ))}
      {dropped > 0 && (
        <div className="ed-muted" style={{ marginTop: 6, fontSize: 12 }}>
          +{dropped} more name match{dropped === 1 ? '' : 'es'} not shown —
          settle these first and more will surface.
        </div>
      )}
    </div>
  );
}

/* The 'Clean up duplicates…' merge-proposals view is GONE (2026-07-06):
   the unified resolve engine + the inline cleanup lane superseded the
   parallel graph-level dedup queue. Undo rides the witness_merge_ops
   ledger (merges/undo) via useWitnessRoster/useWitnessCleanup. */

// Recently-merged / undo surface. Lives wherever a human acts on merges — in the
// roster (the lawyer's one surface) and in the advanced cleanup view. One row
// per recorded merge op (witness_merge_ops — the assertion-model ledger); one
// Undo moves that op's exact fragments back and restores the loser card.
// CONTROLLED: the host owns the `merges` list (so the roster and
// recently-merged share one source of truth — no stale second copy) and
// supplies `onUndo([op_id])` which reverses the op and reloads.
function RecentlyMergedList({ merges, onUndo }) {
  const [undoingId, setUndoingId] = useState(null);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const undo = useCallback(async (m) => {
    if (!window.confirm(
      `Undo this merge into "${m.survivor_name}"?\n\n`
      + 'This restores the merged-away people as separate everywhere in the case.'
    )) return;
    setError(''); setUndoingId(m.op_id);
    try {
      await onUndo([m.op_id]);
    } catch (e) {
      if (mountedRef.current) setError(e.message || 'Undo failed');
    } finally {
      if (mountedRef.current) setUndoingId(null);
    }
  }, [onUndo]);

  const applied = merges.filter((m) => !m.undone);
  if (applied.length === 0) return null;
  return (
    <div style={{ marginTop: 28, borderTop: '1px solid var(--border, #e5e7eb)', paddingTop: 14 }}>
      <h4 className="wt-col-title" style={{ margin: '0 0 4px' }}>Recently merged</h4>
      <p className="ed-muted" style={{ marginTop: 0, fontSize: 12 }}>
        Each row is one merge. <strong>Undo</strong> restores the merged-away
        people as separate everywhere — Ask Arc, the chronology, and dossiers.
      </p>
      {error && (
        <div style={{ color: '#b91c1c', background: '#fee2e2', padding: '6px 10px', borderRadius: 6, margin: '8px 0', fontSize: 13 }}>{error}</div>
      )}
      {applied.map((m) => {
        const n = m.moved_count || 1;
        return (
          <div
            key={m.op_id}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--border, #e5e7eb)', borderRadius: 6, marginBottom: 6, fontSize: 13 }}
          >
            <span style={{ flex: 1 }}>
              <strong>{m.loser_name}</strong>
              <span className="ed-muted"> merged into </span>
              <strong>{m.survivor_name}</strong>
              <span className="ed-muted"> ({n} reference{n > 1 ? 's' : ''})</span>
              {m.created_at && (
                <span className="ed-muted" style={{ marginLeft: 8, fontSize: 11 }}>
                  {new Date(m.created_at).toLocaleString()}
                </span>
              )}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => undo(m)}
              disabled={undoingId === m.op_id}
              title="Reverse this merge — restore the merged-away people case-wide"
            >
              {undoingId === m.op_id ? 'Undoing…' : '↩ Undo'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/* ── Inline cleanup lane ──
   Arc's bounded cleanup renders INTO the roster (top of the roster column),
   not over it in a modal. While Arc reasons it's a quiet background banner;
   when the plan lands it's a per-row approval lane (reason + citations + verify
   badge), approved in place. Driven by the useWitnessCleanup hook in the tab,
   so the run survives navigating elsewhere in the tab. Reuses the .wcp-* row
   styles the modal used. Seam consolidation step B. */
function CleanupOpRow({ op, off, onToggle, pathname }) {
  return (
    <label className={`wcp-row ${off ? 'wcp-row-off' : ''}`}>
      <input type="checkbox" checked={!off} onChange={() => onToggle(op.id)} />
      <div className="wcp-row-body">
        <div className="wcp-row-top">
          <span className={`wcp-op wcp-op-${op.op}`}>{OP_LABEL[op.op] || op.op}</span>
          <span className="wcp-people">{(op.people || []).join(' + ') || '—'}</span>
          {typeof op.confidence === 'number' && (
            <span className="wcp-conf">{Math.round(op.confidence * 100)}%</span>
          )}
          {op.verify && (op.verify.held
            ? <span className="wcp-verified" title="An independent check confirmed this merge">✓ Verified</span>
            : <span className="wcp-flagged" title={op.verify.reason || 'An independent check could not confirm this'}>⚠ Unconfirmed</span>
          )}
          {(op.evidence_tier === 'surface' || op.evidence_tier === 'mixed') && (
            <span
              className="wcp-tier"
              title="Some cited evidence is from surface-tier (only partially read) documents — a deep read may surface more before you decide."
            >📄 {op.evidence_tier === 'mixed' ? 'partly surface-tier' : 'surface-tier'}</span>
          )}
        </div>
        {op.reason && <div className="wcp-reason">{op.reason}</div>}
        {op.citations?.length > 0 && (
          <div className="wcp-cites">
            {op.citations.map((c, j) => <CitationLink key={j} item={c} pathname={pathname} />)}
          </div>
        )}
        {/* S2: when the independent check couldn't confirm a merge, show WHY +
            its sources so the human can verify and override. */}
        {op.verify && !op.verify.held && (
          <div className="wcp-verify-flag">
            <div className="wcp-verify-flag-reason">⚠ Couldn’t confirm: {op.verify.reason}</div>
            {op.verify.citations?.length > 0 && (
              <div className="wcp-cites">
                {op.verify.citations.map((c, j) => <CitationLink key={j} item={c} pathname={pathname} />)}
              </div>
            )}
            <div className="wcp-verify-flag-hint">Check the box to merge anyway, or leave it to skip.</div>
          </div>
        )}
      </div>
    </label>
  );
}

function WitnessCleanupLane({ cleanup, pathname }) {
  const {
    phase, targets, plan, progress, result, error,
    rejectedIds, approvedIds, undoneOps, undoingOp,
    toggleRow, apply, undo, cancel, dismiss,
  } = cleanup;
  if (phase === 'idle') return null;
  const n = targets?.length ?? 0;

  if (phase === 'starting' || phase === 'running' || phase === 'applying') {
    return (
      <div className="wt-cleanup-lane wt-cleanup-working">
        <span className="wcp-spinner" />
        <span className="wt-cleanup-progress">{progress}</span>
        <button type="button" className="btn btn-sm" onClick={cancel}>Cancel</button>
      </div>
    );
  }

  if (phase === 'ready' && plan) {
    return (
      <div className="wt-cleanup-lane">
        <div className="wt-cleanup-head">
          <span className="wt-cleanup-title">✨ Arc’s sweep — {n} witness{n === 1 ? '' : 'es'}</span>
          <span className="wcp-count">{approvedIds.length} of {plan.ops.length} selected</span>
        </div>
        {plan.summary && <div className="wcp-summary">{plan.summary}</div>}
        <div className="wcp-rows wt-cleanup-rows">
          {plan.ops.map((op) => (
            <CleanupOpRow
              key={op.id}
              op={op}
              off={rejectedIds.has(op.id)}
              onToggle={toggleRow}
              pathname={pathname}
            />
          ))}
        </div>
        <div className="wcp-foot">
          {/* Only reached when NOTHING was verifiable — verified ops apply
              themselves. This lane is genuine human judgment, not a rubber
              stamp. */}
          <span className="ed-muted" style={{ fontSize: 12 }}>Arc couldn’t independently verify these — tick only the ones you’re sure of.</span>
          <div className="wcp-foot-actions">
            <button type="button" className="btn btn-sm" onClick={cancel}>Dismiss</button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={approvedIds.length === 0}
              onClick={apply}
            >Apply {approvedIds.length} change{approvedIds.length === 1 ? '' : 's'}</button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'empty') {
    return (
      <div className="wt-cleanup-lane wt-cleanup-note">
        <span>{plan?.summary || 'Arc found no duplicates in this selection.'}</span>
        <button type="button" className="btn btn-sm" onClick={dismiss}>Dismiss</button>
      </div>
    );
  }

  if (phase === 'done') {
    const appliedOps = result?.applied_ops || [];
    return (
      <div className="wt-cleanup-lane">
        <div className="wcp-progress" style={{ padding: '10px 12px' }}>
          ✅ Applied {result?.applied ?? 0} change{(result?.applied ?? 0) === 1 ? '' : 's'}
          {result ? ` (${result.merged} merged, ${result.accepted} accepted, ${result.rejected} rejected).` : '.'}
        </div>
        {appliedOps.length > 0 && (
          <div className="wcp-applied">
            {appliedOps.map((op, i) => {
              const isUndone = undoneOps.has(op.op_id);
              return (
                <div
                  key={op.op_id ?? i}
                  className={`wcp-applied-row${isUndone ? ' wcp-applied-undone' : ''}`}
                >
                  <span className={`wcp-op wcp-op-${op.kind}`}>{OP_LABEL[op.kind] || op.kind}</span>
                  <span className="wcp-people">{(op.people || []).join(' + ') || '—'}</span>
                  {op.undoable && !isUndone && (
                    <button
                      type="button"
                      className="btn btn-sm wcp-undo"
                      disabled={undoingOp === op.op_id}
                      onClick={() => undo(op)}
                      title="Reverse this merge — restores the people as separate"
                    >{undoingOp === op.op_id ? 'Undoing…' : '↩ Undo'}</button>
                  )}
                  {isUndone && <span className="wcp-undone-tag">Undone</span>}
                </div>
              );
            })}
          </div>
        )}
        {/* Ops Arc could not independently verify were NOT auto-applied —
            list them so "left separate" is a visible decision, not silence. */}
        {plan?.ops?.some((o) => rejectedIds.has(o.id)) && (
          <div className="wcp-applied">
            {plan.ops.filter((o) => rejectedIds.has(o.id)).map((op, i) => (
              <div key={op.id ?? i} className="wcp-applied-row wcp-applied-undone">
                <span className={`wcp-op wcp-op-${op.kind}`}>{OP_LABEL[op.kind] || op.kind}</span>
                <span className="wcp-people">{(op.people || []).join(' + ') || '—'}</span>
                <span className="wcp-undone-tag" title={op.verify?.reason || 'Arc could not independently verify this merge'}>Left separate — not verified</span>
              </div>
            ))}
          </div>
        )}
        {result?.errors?.length > 0 && (
          <div className="bulk-popover-error">{result.errors.length} op(s) failed — see logs.</div>
        )}
        <div className="wcp-foot">
          <span style={{ flex: 1 }} />
          <button type="button" className="btn btn-sm btn-primary" onClick={dismiss}>Done</button>
        </div>
      </div>
    );
  }

  // error
  return (
    <div className="wt-cleanup-lane wt-cleanup-note">
      <span className="bulk-popover-error" style={{ flex: 1 }}>{error}</span>
      <button type="button" className="btn btn-sm" onClick={dismiss}>Dismiss</button>
    </div>
  );
}

/* ── E2 — the Review queue is gone (Invisible Resolution): the machine acts,
   the human corrects by EXCLUDING (✂ on a reference row → durable NOT_SAME,
   the pair can never silently rejoin — and it teaches the learner a
   negative). Positive labels come from Confirm this person / manual merges. ── */
function WitnessViewBar({ onTidy, tidying }) {
  // The bar exists only to host the manual Tidy fallback. The "Witnesses"
  // tab-within-a-tab label it used to carry was pure chrome (a single inner
  // tab duplicating the page tab) — gone; with auto-resolve on, so is the bar.
  if (!onTidy) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
      <span style={{ flex: 1 }} />
      {/* The whole-roster sweep (Phase D1) — the manual fallback while the
          auto-resolve flag is off; hidden when the background loop owns
          tidying. Delete with the flag once auto-resolve is on in prod. */}
      {onTidy && (
        <button
          type="button"
          onClick={onTidy}
          disabled={tidying}
          title="One pass over the whole list: fold every decisive duplicate (spelling/format variants, unique bare names). Reversible — split anything wrong afterward."
          style={{
            cursor: tidying ? 'default' : 'pointer', fontSize: 13, fontWeight: 600,
            color: tidying ? '#94a3b8' : 'var(--accent, #2563eb)',
            border: '1px solid', borderColor: tidying ? '#e2e8f0' : 'var(--accent, #2563eb)',
            borderRadius: 8, background: '#fff', padding: '5px 12px',
            marginRight: 14,
          }}
        >{tidying ? 'Tidying…' : '✨ Tidy roster'}</button>
      )}
    </div>
  );
}

/* ── Tab ── */

/* C3 coherence pass: the case's decision ledger — every ruling (yours and
 * Arc's awaiting review) plus every still-open role conflict, in one strip
 * above the roster. Decisions only compound if the attorney can SEE them
 * land (doctrine: no decision wasted); this is the single answer to
 * "what have I corrected and what's still contested?". */
function CorrectionsLedger({ caseId }) {
  const { profile: userProfile } = useUserProfile();
  const adjAgentOn = !!userProfile?.features?.fact_adjudication_agent;
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [note, setNote] = useState(null);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const load = useCallback(async () => {
    try {
      setData(await api.get(`/cases/${caseId}/adjudications`));
    } catch {
      setData(null);
    }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const rulings = data.rulings || [];
  const pending = rulings.filter(r => r.review_state === 'AI_APPLIED_UNREVIEWED');
  const confirmed = rulings.filter(r => r.review_state === 'HUMAN_CONFIRMED');
  const openConflicts = (data.conflicts || []).filter(
    c => c.severity !== 'resolved' && !c.pending_adjudication,
  );
  if (!rulings.length && !openConflicts.length) return null;

  async function review(id, action) {
    setBusyKey(id);
    try {
      await api.post(`/cases/${caseId}/adjudications/${id}/review`, { action });
      if (aliveRef.current) await load();
    } catch (e) {
      if (aliveRef.current) setNote(e.message || 'Review failed.');
    } finally {
      if (aliveRef.current) setBusyKey(null);
    }
  }

  async function sortOut(name) {
    setBusyKey(`sort:${name}`);
    setNote(null);
    try {
      const started = await api.post(`/cases/${caseId}/adjudications/agent-run`, { name });
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        if (!aliveRef.current) return;
        const run = await api.get(`/cases/${caseId}/adjudications/agent-run/${started.run_id}`);
        if (run.status === 'running') continue;
        if (run.status !== 'recorded') setNote(run.detail || 'Arc could not settle this.');
        await load();
        break;
      }
    } catch (e) {
      if (aliveRef.current) setNote(e.message || 'Could not start the investigation.');
    } finally {
      if (aliveRef.current) setBusyKey(null);
    }
  }

  const conflictLine = (c) => Object.entries(c.buckets || {})
    .map(([b, slot]) => `${slot.document_count} ${b.toLowerCase()}`).join(', ');

  return (
    <div style={{
      margin: '8px 12px 0', border: '1px solid #e2e2ea', borderRadius: 8,
      background: '#fafafc', fontSize: 13,
    }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none',
          padding: '8px 12px', cursor: 'pointer', fontSize: 13,
        }}
        title="Everything you've corrected, Arc's suggestions waiting for your OK, and anything the system read inconsistently"
      >
        {open ? '▾' : '▸'} <b>✔ Corrections</b>
        {' — '}{confirmed.length} made
        {pending.length > 0 && <> · <b style={{ color: '#4f46e5' }}>{pending.length} suggested (need{pending.length === 1 ? 's' : ''} your OK)</b></>}
        {openConflicts.length > 0 && <> · <span style={{ color: '#b45309' }}>{openConflicts.length} to review</span></>}
      </button>
      {open && (
        <div style={{ padding: '0 12px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {confirmed.map(r => (
            <div key={r.adjudication_id} style={{ color: '#16825d' }}>
              ✔ {r.assertion}
              <span style={{ opacity: 0.7 }}> — {(r.subject_names || []).join(', ')}</span>
            </div>
          ))}
          {pending.map(r => (
            <div key={r.adjudication_id} style={{ color: '#4f46e5' }}>
              🤖 {r.assertion}
              <span style={{ marginLeft: 8 }}>
                <button className="btn btn-sm btn-primary" disabled={busyKey === r.adjudication_id}
                  onClick={() => review(r.adjudication_id, 'confirm')}>Confirm</button>
                {' '}
                <button className="btn btn-sm" disabled={busyKey === r.adjudication_id}
                  onClick={() => review(r.adjudication_id, 'reject')}>Reject</button>
              </span>
            </div>
          ))}
          {openConflicts.map(c => (
            <div key={c.name} style={{ color: '#b45309' }}>
              ⚠ {c.name}: read differently across documents ({conflictLine(c)})
              {adjAgentOn && (
                busyKey === `sort:${c.name}`
                  ? <span style={{ marginLeft: 8, opacity: 0.8 }}>🤖 Arc is reading the sources…</span>
                  : <button className="btn btn-sm" style={{ marginLeft: 8 }}
                      onClick={() => sortOut(c.name)}>⚖ Sort this out with Arc</button>
              )}
            </div>
          ))}
          {note && <div className="ed-muted" style={{ fontSize: 12 }}>{note}</div>}
        </div>
      )}
    </div>
  );
}

export default function WitnessesTab({ caseId }) {
  const { pathname } = useLocation();
  const [activeId, setActiveId] = useState(null);
  // Single source of truth for the roster + recently-merged ledger + all writes
  // against them. UI state (selection, filters, active row) stays local.
  const roster = useWitnessRoster(caseId, { onActivate: setActiveId });
  const {
    accepted, suggested, applied,
    loading, setLoading, busy, error, setError,
    loadRoster, loadApplied, reloadAll,
    suggest, review, merge, mergeMany, split, moveRefs, patch, undoMerge,
  } = roster;
  // Multi-select for the "Resolve N selected" action bar (witness ids).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  // Workspace focus: `drilled` = the user opened one card (detail); otherwise, a
  // non-empty selection shows the Batch Overview so you can browse the batch.
  // Selection changes reset to the overview; clicking a card drills in. The
  // single-card DetailPanel is never lost — it nests under a "‹ Selected" crumb.
  const [drilled, setDrilled] = useState(false);
  // Bumped on any Workbench action (assemble / separate / split) so the open
  // DetailPanel re-fetches its evidence + "Made up of" — the survivor's id is
  // unchanged after an in-place assemble, so a roster reload alone wouldn't.
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const handleDetailChanged = useCallback(() => {
    reloadAll();
    setDetailReloadKey((k) => k + 1);
  }, [reloadAll]);
  // Phase D1 — the whole-roster conservative sweep. One resolver pass folded
  // into every card it touches; decisive matches only (the measured P=1.0
  // band). Reversible like every witness write — Split undoes and sticks.
  const [sweeping, setSweeping] = useState(false);
  const [sweepNote, setSweepNote] = useState(null); // { kind, text }
  // The manual Arc-pass button (Phase D2, policy 'balanced') is GONE: the
  // balanced band showed a blind false merge on each gold case and was retired
  // from the engine — the LLM tier is the calibrated "Go deep" offer below.
  const runRosterSweep = useCallback(async () => {
    if (!window.confirm(
      'Tidy the whole roster?\n\nArc folds every decisive duplicate — spelling '
      + 'and format variants, plus bare first names that can only be one '
      + 'person — across the entire list in one pass. Judgment calls are left '
      + 'for you.\n\nEverything is reversible: split anything wrong afterward '
      + 'and it stays split.',
    )) return;
    setSweeping(true); setSweepNote(null);
    try {
      const out = await api.post(`/cases/${caseId}/witnesses/resolve-roster`, {});
      if (out.timing) console.log('[timing:roster-sweep]', out.timing);
      const n = out.merge_count || 0;
      if (n) {
        const least = (out.merges || [])[0];
        const start = least?.survivor_name ? ` Start with “${least.survivor_name}” — it's the least-sure fold.` : '';
        setSweepNote({
          kind: 'ok',
          text: `Folded ${n} duplicate group${n === 1 ? '' : 's'} — roster went `
            + `${out.cards_before} → ${out.cards_after} people.${start} Review the merged `
            + `cards and split anything that doesn't belong.`,
        });
        reloadAll();
        setDetailReloadKey((k) => k + 1);
      } else {
        setSweepNote({ kind: 'info', text: 'No decisive duplicates left — the roster is already tidy at this setting.' });
      }
    } catch (e) {
      setSweepNote({ kind: 'err', text: e.message || 'Roster tidy failed' });
    } finally {
      setSweeping(false);
    }
  }, [caseId, reloadAll]);
  // Phase E1 — Invisible Resolution. On load, ask the backend to make sure the
  // roster is tidy: fresh watermark = instant no-op; stale = the D1+D2 loop
  // runs server-side while a quiet strip shows progress and we poll. When the
  // flag is off the endpoint answers {enabled:false} and the manual Tidy/Arc
  // buttons stay exactly as they are — no split-brain.
  const [autoTidy, setAutoTidy] = useState(null); // null = manual mode
  const autoTidyWasRunning = useRef(false);
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const out = await api.post(`/cases/${caseId}/witnesses/ensure-resolved`);
        if (cancelled) return;
        if (!out.enabled) { setAutoTidy(null); return; }
        setAutoTidy(out);
        if (out.status === 'started' || out.status === 'running') {
          autoTidyWasRunning.current = true;
          timer = setTimeout(tick, 4000);
        } else if (autoTidyWasRunning.current) {
          // Just finished — pull in what the loop folded, exactly once. Also
          // refresh the open detail panel: waves rehome references while a
          // card is open, and a stale panel reads as "my references vanished".
          autoTidyWasRunning.current = false;
          reloadAll();
          setDetailReloadKey((k) => k + 1);
          if (out.status === 'fresh' && (out.folds || 0) > 0) {
            setSweepNote({
              kind: 'ok',
              text: `Arc tidied the roster automatically — ${out.folds} fold${out.folds === 1 ? '' : 's'}`
                + `${out.held ? `, ${out.held} big cluster${out.held === 1 ? '' : 's'} held back` : ''}.`
                + ' Split anything that doesn\'t belong.',
            });
          } else if (out.status === 'failed') {
            // Never vanish silently — a died run looks identical to a clean
            // finish otherwise (learned on the Brenner idle-txn failure).
            setSweepNote({
              kind: 'err',
              text: out.last_error
                || 'The automatic tidy hit a problem — it will retry on the next load.',
            });
          }
        }
      } catch { if (!cancelled) setAutoTidy(null); /* fall back to manual */ }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [caseId, reloadAll]);
  const autoTidyOn = !!autoTidy;
  const autoTidyRunning = autoTidyOn
    && (autoTidy.status === 'started' || autoTidy.status === 'running');

  // ── The "go deep" offer (tier split) ────────────────────────────────────
  // The free tier grouped everything name-safe and COUNTED the residue
  // (open_questions). Spending on the LLM tier is the user's decision: show
  // the count with a real quote (priced from this case's own metered calls),
  // one button starts the background deep run; progress rides the same
  // ensure-resolved poll above.
  const openQuestions = (!autoTidyRunning && autoTidy?.open_questions) || 0;
  const [deepQuote, setDeepQuote] = useState(null);
  const [deepStarting, setDeepStarting] = useState(false);
  const [deepDismissed, setDeepDismissed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setDeepQuote(null);
    if (!openQuestions) return undefined;
    (async () => {
      try {
        const q = await api.get(`/cases/${caseId}/witnesses/resolve-quote`);
        if (!cancelled) setDeepQuote(q);
      } catch { /* offer renders without a price */ }
    })();
    return () => { cancelled = true; };
  }, [caseId, openQuestions]);
  const startDeep = useCallback(async () => {
    setDeepStarting(true);
    try {
      const out = await api.post(`/cases/${caseId}/witnesses/resolve-deep`);
      autoTidyWasRunning.current = true;
      setAutoTidy(out.enabled === false ? null : out);
    } catch {
      setSweepNote({ kind: 'err', text: 'Could not start the deep resolve — try again.' });
    } finally {
      setDeepStarting(false);
    }
  }, [caseId]);

  // ── Roster health (deterministic lint — free) ───────────────────────────
  // One fetch per roster change; per-card counts feed the ⚕ badge via row
  // enrichment (witnessLayers 'health' layer reads w.lint_count).
  const [lintByWitness, setLintByWitness] = useState({});
  const [lintFindings, setLintFindings] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rep = await api.get(`/cases/${caseId}/witnesses/roster-lint`);
        if (!cancelled) {
          setLintByWitness(rep.per_witness || {});
          setLintFindings(rep.findings || []);
        }
      } catch {
        if (!cancelled) { setLintByWitness({}); setLintFindings([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, detailReloadKey, autoTidyRunning]);
  // Bounded Arc cleanup run, owned by the roster so it renders INLINE (a lane at
  // the top of the roster) instead of a modal — Arc proposes into the tab. On
  // apply/undo it reloads the roster + recently-merged ledger. onApplied is
  // memoized so the tab's frequent re-renders (search keystrokes) don't churn
  // the event-handler identity mid-run.
  const onCleanupApplied = useCallback(() => {
    clearSelection(); reloadAll();
  }, [clearSelection, reloadAll]);
  const cleanup = useWitnessCleanup(caseId, { onApplied: onCleanupApplied });
  // Default-hide the extractor's descriptive noise (role_reference / junk).
  const [hideNoise, setHideNoise] = useState(true);
  // The index's sort (shared selector). The filter rail + search are namespaced
  // `rf` in the URL — the "narrow to a bounded subset" half of the workflow.
  const [sugSort, setSugSort] = useState(WITNESS_SORT.SALIENCE);
  const [rosFilters, setRosFilters] = useState(readRosterFiltersFromUrl);
  const [rosSearch, setRosSearch] = useState('');
  const [autoBuilding, setAutoBuilding] = useState(false);
  // Fires once per mount: guards the empty-tab auto-build from double-running.
  const autoBuiltRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      let all = [];
      try {
        all = await loadRoster();
        if (cancelled) return;
        loadApplied(); // recently-merged ledger — non-blocking
        const firstAccepted = all.find((w) => w.review_state === 'accepted');
        if (firstAccepted) setActiveId(firstAccepted.id);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load witnesses');
        return;
      } finally {
        // Render the tab as soon as the roster is in — a first-open auto-build
        // (entity grouping + LLM classification) can take 30s+, and blocking the
        // whole tab on it reads as a hang. The Suggestions column shows its own
        // skeleton via `autoBuilding` instead.
        if (!cancelled) setLoading(false);
      }

      // Auto-build on first open: a genuinely empty tab (nothing accepted AND
      // nothing suggested) kicks off a suggest run so the user doesn't have to
      // click. Guarded by a ref so it runs at most once per mount. Runs AFTER
      // loading is cleared so it's non-blocking.
      const isEmpty = !all.some(
        (w) => w.review_state === 'accepted' || w.review_state === 'suggested',
      );
      if (isEmpty && !autoBuiltRef.current) {
        autoBuiltRef.current = true;
        setAutoBuilding(true);
        try {
          await api.post(`/cases/${caseId}/witnesses/suggest?limit=60`);
          if (!cancelled) await loadRoster();
        } catch (e) {
          if (!cancelled) setError(e.message || 'Could not build the roster.');
        } finally {
          if (!cancelled) setAutoBuilding(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, loadRoster]);

  const toggleSelect = useCallback((witness) => {
    setDrilled(false);  // a selection change returns focus to the batch overview
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(witness.id)) next.delete(witness.id);
      else next.add(witness.id);
      return next;
    });
  }, []);

  // Select / clear the whole filtered subset — the "narrow then bulk" gesture.
  const toggleSelectAll = useCallback((rows) => {
    setDrilled(false);
    setSelectedIds((prev) => {
      const allSelected = rows.length > 0 && rows.every((w) => prev.has(w.id));
      if (allSelected) {
        const next = new Set(prev);
        for (const w of rows) next.delete(w.id);
        return next;
      }
      return new Set([...prev, ...rows.map((w) => w.id)]);
    });
  }, []);

  // Hand a bounded subset to Arc → the inline cleanup lane (drawer.witness_refine
  // scoped to exactly these people). Arc proposes merge/accept/reject ops; the
  // human approves per-row in the lane. Reversible via the merge ledger.
  const openCleanup = cleanup.start;

  // Roster mutations (suggest/review/merge/split/patch/undoMerge) live in the
  // useWitnessRoster hook above — one shared store, so the roster and the
  // recently-merged ledger never drift. The component only adapts call signatures
  // where it needs the current `activeId` (merge moves focus off the loser).
  const mergeWitness = useCallback(
    (loser, survivorId) => merge(loser, survivorId, activeId),
    [merge, activeId],
  );

  // Roster filter rail callbacks (namespaced `rf` in the URL). FilterRail's onAdd
  // hands us a single {axis, value} object (NOT two positional args) — destructure
  // it, or the chip is built from garbage and matches nothing.
  const addRosFilter = useCallback(({ axis, value }) => {
    setRosFilters((f) => (f.some((c) => c.axis === axis && c.value === value)
      ? f
      : [...f, { axis, value }]));
  }, []);
  const removeRosFilter = useCallback((axis, value) => {
    setRosFilters((f) => f.filter((c) => !(c.axis === axis && c.value === value)));
  }, []);
  const clearRosterFilters = useCallback(() => setRosFilters([]), []);
  useEffect(() => { writeRosterFiltersToUrl(rosFilters); }, [rosFilters]);

  // The selected witness can be an accepted card OR a still-suggested one
  // (active or deferred), so a clicked suggestion stays shown in the detail pane.
  const active = useMemo(
    () => accepted.find((w) => w.id === activeId)
      || suggested.find((w) => w.id === activeId)
      || null,
    [accepted, suggested, activeId],
  );

  // If the selected witness left every visible list (e.g. just dismissed), drop
  // the selection so the detail pane doesn't pin a stale row.
  useEffect(() => {
    if (activeId != null && !active) setActiveId(null);
  }, [activeId, active]);

  // Merge targets for the manual picker: accepted witnesses first (sorted), then
  // the remaining suggestions. The caller excludes the card itself per-render.
  const mergeTargetPool = useMemo(() => {
    const acc = accepted.slice().sort(compareWitnesses);
    return [...acc, ...suggested];
  }, [accepted, suggested]);
  const mergeTargetsFor = useCallback(
    (witness) => mergeTargetPool.filter((w) => w.id !== witness.id),
    [mergeTargetPool],
  );

  // ── Unified index (Workbench W1) ──
  // ONE flat list of all entity-cards (accepted + suggested) — status/abstraction
  // are facets, not separate columns. Filter via the rail (rf) + search; sort via
  // the shared selector. Replaces the old Suggestions + Roster split.
  const indexFiltered = useMemo(() => {
    let out = applyWitnessFilters([...accepted, ...suggested], rosFilters);
    const q = rosSearch.trim().toLowerCase();
    if (q) {
      out = out.filter((w) =>
        (w.display_name || '').toLowerCase().includes(q)
        || (w.aliases || []).some((a) => String(a).toLowerCase().includes(q)));
    }
    return out.slice().sort(compareWitnessesBy(sugSort));
  }, [accepted, suggested, rosFilters, rosSearch, sugSort]);
  // Default-hide descriptive noise (the extractor's `role_reference` / low-conf
  // `junk` — "a male individual", "a bald man with a beard"). The backend already
  // hides HIGH-confidence junk (demote-don't-delete); this hides the rest from
  // the default view. Toggle off to see everything. Never hides `unclassified`
  // (could be a real person the LLM hasn't judged) or `real_person` / `group`.
  const indexCards = useMemo(
    () => {
      const rows = hideNoise
        ? indexFiltered.filter((w) => !NOISE_QUALITY.has(w.quality_label))
        : indexFiltered;
      // Enrich with roster-lint counts — the ⚕ health badge (witnessLayers)
      // reads lint_count off the row.
      return rows.map((w) => (
        lintByWitness[w.id] ? { ...w, lint_count: lintByWitness[w.id] } : w
      ));
    },
    [indexFiltered, hideNoise, lintByWitness],
  );
  const noiseHidden = indexFiltered.length - indexCards.length;
  const totalCards = accepted.length + suggested.length;
  const indexHasFilter = rosFilters.length > 0 || rosSearch.trim() !== '';
  const indexAllSelected = indexCards.length > 0 && indexCards.every((w) => selectedIds.has(w.id));
  const indexSelected = useMemo(
    () => [...accepted, ...suggested].filter((w) => selectedIds.has(w.id)),
    [accepted, suggested, selectedIds],
  );
  const indexEstimate = useMemo(() => estimateCleanupSize(indexSelected), [indexSelected]);
  // Workspace shows the Batch Overview when there's a selection and the user
  // hasn't drilled into a single card. Drilling (or clearing) returns to detail.
  const batchMode = selectedIds.size >= 1 && !drilled;

  // Bulk merge the selected people into one (survivor = most-documented). The
  // deterministic "I know these are the same" path — no Arc, no size gate.
  const mergeSelected = useCallback(() => {
    const list = indexSelected;
    if (list.length < 2) return;
    const survivor = [...list].sort(
      (a, b) => (b.evidence_count || 0) - (a.evidence_count || 0),
    )[0];
    if (!window.confirm(
      `Merge these ${list.length} people into one ("${survivor.display_name}")?\n\n`
      + 'This combines them everywhere in the case — Ask Arc, the chronology, and '
      + 'dossiers. You can undo it from "Recently merged".'
    )) return;
    Promise.resolve(mergeMany(list, survivor.id)).finally(() => clearSelection());
  }, [indexSelected, mergeMany, clearSelection]);

  if (loading) return <div className="ed-muted" style={{ padding: 16 }}>Loading…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <WitnessViewBar onTidy={autoTidyOn ? null : runRosterSweep} tidying={sweeping} />
      <CorrectionsLedger caseId={caseId} />
      <div className="wb-wrap" style={{ flex: 1, minHeight: 0 }}>
        {/* ── Index — one flat list of entity-cards (status/abstraction are
            facets, not separate columns). Pick one → its Workbench opens. ── */}
        <div className="wb-index">
          <div className="wb-index-head">
            <h3 className="wt-col-title">Witnesses</h3>
            <span className="wt-col-count">
              {indexCards.length}{indexHasFilter ? ` of ${totalCards}` : ''}
            </span>
            <span style={{ flex: 1 }} />
            {indexCards.length > 0 && (
              <button
                type="button"
                className="btn btn-sm wt-select-all"
                onClick={() => toggleSelectAll(indexCards)}
                title="Select / clear the filtered set for a batch action"
              >{indexAllSelected ? 'Clear' : 'Select all'}</button>
            )}
            <button
              className="btn btn-sm wt-refresh-btn"
              onClick={suggest}
              disabled={busy || autoBuilding || sweeping}
              title="Re-run discovery to pull newly-found people into the roster"
            >{busy ? 'Working…' : '🔄 Find people'}</button>
            <WitnessExportMenu caseId={caseId} acceptedCount={accepted.length} disabled={autoBuilding} />
          </div>
          {autoTidyRunning && (
            // E1 — the quiet strip: the background loop is tidying; rows
            // refresh when it finishes. No buttons, nothing to decide.
            <div className="wt-cleanup-lane wt-cleanup-note">
              <span style={{ flex: 1 }}>
                ✨ Arc is tidying the roster…
                {autoTidy.folds > 0 && ` ${autoTidy.folds} fold${autoTidy.folds === 1 ? '' : 's'} so far`}
                {autoTidy.waves > 0 && ` (wave ${autoTidy.waves})`}
              </span>
            </div>
          )}
          {openQuestions > 0 && !deepDismissed && !cleanup.active && (
            // The go-deep offer: the free pass grouped everything name-safe
            // and counted what needs document reads. Spending is the user's
            // call — one button, a real quote, resumable any time.
            <div className="wt-cleanup-lane wt-cleanup-note">
              <span style={{ flex: 1 }}>
                🔎 {openQuestions} identity question{openQuestions === 1 ? '' : 's'} need
                {openQuestions === 1 ? 's' : ''} document reads to settle
                {deepQuote?.est_cost_usd != null && ` (~$${deepQuote.est_cost_usd.toFixed(2)}`}
                {deepQuote?.est_seconds != null && deepQuote?.est_cost_usd != null
                  && `, ~${Math.max(1, Math.round(deepQuote.est_seconds / 60))} min)`}
                . The roster is usable now — going deep just settles the rest.
              </span>
              <button
                type="button" className="btn btn-sm"
                onClick={startDeep}
                disabled={deepStarting}
                title="Arc reads the documents behind each open question and settles what the evidence supports. Runs in the background; you can keep working."
                style={{ color: 'var(--accent, #2563eb)', fontWeight: 600 }}
              >{deepStarting ? '⏳ Starting…' : '🔎 Go deep'}</button>
              <button type="button" className="btn btn-sm" onClick={() => setDeepDismissed(true)}>Later</button>
            </div>
          )}
          {sweepNote && (
            <div className="wt-cleanup-lane wt-cleanup-note">
              <span style={{ flex: 1 }} className={sweepNote.kind === 'err' ? 'bulk-popover-error' : undefined}>
                {sweepNote.text}
              </span>
              <button type="button" className="btn btn-sm" onClick={() => setSweepNote(null)}>Dismiss</button>
            </div>
          )}

          <div className="wt-sug-controls">
            <div className="wt-sug-controls-row">
              <div className="wt-sug-search">
                <input
                  type="text"
                  className="input wt-sug-search-input"
                  placeholder="Search name or alias…"
                  value={rosSearch}
                  onChange={(e) => setRosSearch(e.target.value)}
                />
                {rosSearch && (
                  <button
                    type="button"
                    className="wt-sug-search-clear"
                    onClick={() => setRosSearch('')}
                    aria-label="Clear search"
                  >✕</button>
                )}
              </div>
              <label className="wt-sug-sort">
                <span className="wt-sug-sort-label">Sort</span>
                <select className="input" value={sugSort} onChange={(e) => setSugSort(e.target.value)}>
                  {Object.entries(WITNESS_SORT_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </label>
            </div>
            <FilterRail
              filters={rosFilters}
              documents={accepted.concat(suggested)}
              docsForFacets={accepted.concat(suggested)}
              axes={WITNESS_AXES}
              getRows={getWitnessPickerRows}
              addHint=" — Status, Importance, Type, Duplicate risk, …"
              onAdd={addRosFilter}
              onRemove={removeRosFilter}
              onClearAll={clearRosterFilters}
            />
            {(hideNoise || noiseHidden > 0) && (
              <label className="wb-noise-toggle" title="Hide the extractor's descriptive non-people (e.g. “a male individual”). Real and unjudged entities always stay.">
                <input
                  type="checkbox"
                  checked={hideNoise}
                  onChange={(e) => setHideNoise(e.target.checked)}
                />
                <span>Hide generic mentions{hideNoise && noiseHidden > 0 ? ` (${noiseHidden} hidden)` : ''}</span>
              </label>
            )}
          </div>

          {/* Arc proposes INTO the index — the cleanup lane sits at the top. */}
          <WitnessCleanupLane cleanup={cleanup} pathname={pathname} />

          {/* Discoverability: multi-select is how you reach the bulk merge/resolve
              action. Show the affordance before anything is selected. */}
          {selectedIds.size === 0 && indexCards.length > 0 && !cleanup.active && (
            <div className="wt-bulk-hint">
              ☑ Tick people to <b>merge</b> or <b>resolve duplicates</b> as a batch.
            </div>
          )}

          <div className="wb-index-list">
            {error && <div className="bulk-popover-error">{error}</div>}
            {autoBuilding && totalCards === 0 ? (
              <div className="wt-skeleton">
                <div className="wt-skel-line" />
                <div className="wt-skel-card" />
                <div className="wt-skel-card" />
                <div className="wt-skel-card" />
              </div>
            ) : totalCards === 0 ? (
              <div className="wt-empty">
                <p>No individuals extracted yet — check back once document processing finishes.</p>
              </div>
            ) : indexCards.length === 0 ? (
              <div className="ed-muted">No entities match the current filters.</div>
            ) : (
              indexCards.map((w) => (
                <IndexCard
                  key={w.id}
                  witness={w}
                  active={w.id === activeId}
                  selected={selectedIds.has(w.id)}
                  onSelect={(x) => { setActiveId(x.id); setDrilled(true); }}
                  onToggleSelect={toggleSelect}
                />
              ))
            )}
          </div>

          {/* Floating bulk bar — only when you've DRILLED into a card (so you can
              resolve without returning to the overview). In batch mode the same
              action lives in the overview header, so hide it here (one dial at a
              time). Also hidden while a cleanup run is live (it would cover the
              lane's Apply button). */}
          {selectedIds.size > 0 && !cleanup.active && !batchMode && (
            <div className={`wt-refine-bar wt-zone-${indexEstimate.zone}`}>
              <div className="wt-refine-info">
                <button type="button" className="wb-crumb" onClick={() => setDrilled(false)}>
                  ‹ Back to Selected ({indexEstimate.count})
                </button>
                <span className={`wt-zone-pill wt-zone-pill-${indexEstimate.zone}`}>
                  {indexEstimate.zone === 'green' && 'Good batch size'}
                  {indexEstimate.zone === 'amber' && `Large — ~${indexEstimate.batches} waves`}
                  {indexEstimate.zone === 'red' && 'Too broad — narrow with filters'}
                </span>
              </div>
              <div className="wt-refine-actions">
                <SweepButton
                  count={indexEstimate.count}
                  zone={indexEstimate.zone}
                  disabled={busy || cleanup.active}
                  onSweep={() => openCleanup(indexSelected)}
                />
                <button type="button" className="btn btn-sm" onClick={clearSelection}>Clear</button>
              </div>
            </div>
          )}

          <div className="wb-index-foot">
            <RecentlyMergedList merges={applied} onUndo={undoMerge} />
          </div>
        </div>

        {/* ── Workbench — Batch Overview for a selection, else the single-card
            work surface. The DetailPanel is unchanged; it just nests under a
            "‹ Selected" crumb when there's a live selection. ── */}
        <div className="wb-main">
          {batchMode ? (
            <BatchOverview
              people={indexSelected}
              estimate={indexEstimate}
              cleanupActive={busy || cleanup.active}
              onOpen={(w) => { setActiveId(w.id); setDrilled(true); }}
              onUntick={toggleSelect}
              onClear={clearSelection}
              onSweep={() => openCleanup(indexSelected)}
              onMergeAll={mergeSelected}
            />
          ) : active ? (
            <>
              {selectedIds.size > 0 && (
                <button type="button" className="wb-crumb wb-crumb-top" onClick={() => setDrilled(false)}>
                  ‹ Selected ({selectedIds.size})
                </button>
              )}
              <DetailPanel
                key={active.id}
                caseId={caseId}
                witness={active}
                pathname={pathname}
                onPatch={patch}
                onSplit={split}
                onMoveRefs={moveRefs}
                onRefine={openCleanup}
                onReview={review}
                onMerge={mergeWitness}
                onOpenWitness={setActiveId}
                mergeTargets={mergeTargetsFor(active)}
                onChanged={handleDetailChanged}
                lintFindings={lintFindings.filter(
                  (f) => f.witness_id === active.id || f.other_witness_id === active.id,
                )}
                reloadKey={detailReloadKey}
                busy={busy}
                cleanupActive={cleanup.active}
              />
            </>
          ) : (
            <div className="wb-empty">
              <p>Pick a person from the list to see everything about them — their statements, identity questions, and every action in one place.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
